import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { handleUppercaseInput } from "@/lib/utils";
import { API, BACKEND_URL, apiFetch } from "@/lib/api";
import ProfitLossReport from "@/components/ProfitLossReport";
import GSTSummaryReport from "@/components/GSTSummaryReport";
import { useAuth } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import OpeningBalancesSection from "@/pages/OpeningBalances";
import { exportRowsToCSV } from "@/lib/csvExport";
import CompleteFinancialStatement from "@/components/CompleteFinancialStatement";
import { toLocalISODate } from "@/lib/dateUtils";
import { SupplierPaymentDialog } from "@/components/SupplierPaymentDialog";
import { AccountStatementDialog } from "@/components/AccountStatementDialog";
import RecurringExpensesSection from "@/components/RecurringExpensesSection";
import AdvancePaymentsPanel from "@/components/AdvancePaymentsPanel";
import {
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package,
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3,
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown, ArrowLeftRight
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";

const ComparisonBadge = ({ pct, prevValue, higherIsBetter }) => {
  if (pct === null || pct === undefined) {
    return (
      <p className="text-xs opacity-75 mt-1" data-testid="comparison-badge-no-prev">
        no prior data (prev ₹{(prevValue || 0).toLocaleString()})
      </p>
    );
  }
  const isUp = pct >= 0;
  const isGood = higherIsBetter ? isUp : !isUp;
  const arrow = isUp ? '▲' : '▼';
  const colorClass = isGood ? 'bg-green-500/30 text-white' : 'bg-red-500/30 text-white';
  return (
    <div className="mt-1">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${colorClass}`}
        data-testid="comparison-badge"
      >
        {arrow} {Math.abs(pct).toFixed(1)}%
      </span>
      <p className="text-xs opacity-75 mt-1">vs prev: ₹{(prevValue || 0).toLocaleString()}</p>
    </div>
  );
};

// Tiny inline SVG sparkline for trend visualization inside the colored cards.
// Renders a smooth polyline of the values; final dot is highlighted.
// On hover, shows a tooltip with the exact date + value at the nearest point.
// Props:
//   values: number[] — data points (one per bucket/day)
//   labels: string[] — date strings (YYYY-MM-DD) aligned with values, for tooltip
//   width, height: SVG canvas size (defaults sized for a card corner)
//   testId: optional data-testid
//   valuePrefix: optional currency prefix for tooltip value (default '₹')
const Sparkline = ({ values = [], labels = [], width = 120, height = 36, testId, valuePrefix = '₹' }) => {
  const [hoverIdx, setHoverIdx] = useState(null);

  if (!values || values.length < 2) {
    return null;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1; // avoid div-by-zero (flat line)
  const stepX = values.length > 1 ? width / (values.length - 1) : width;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - 2 - ((v - min) / span) * (height - 4);
    return [x, y];
  });
  const pathData = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(' ');
  const [lastX, lastY] = points[points.length - 1];
  const areaPath = `${pathData} L ${lastX.toFixed(1)} ${height} L 0 ${height} Z`;

  // Find nearest data point to mouse X
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * width;
    const idx = Math.round(relX / stepX);
    const clamped = Math.max(0, Math.min(values.length - 1, idx));
    setHoverIdx(clamped);
  };

  // Format date as e.g. "Mar 11" for compact tooltip
  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  const formatValue = (v) => {
    const n = Number(v) || 0;
    return valuePrefix + n.toLocaleString();
  };

  const tooltipText = hoverIdx !== null
    ? `${formatDate(labels[hoverIdx])} · ${formatValue(values[hoverIdx])}`
    : null;

  const [hx, hy] = hoverIdx !== null ? points[hoverIdx] : [0, 0];

  return (
    <div className="relative inline-block" data-testid={testId ? `${testId}-wrapper` : undefined}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="opacity-90 cursor-crosshair"
        data-testid={testId}
        aria-hidden="true"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <path d={areaPath} fill="rgba(255,255,255,0.18)" />
        <path d={pathData} fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Static last-point dot when not hovering */}
        {hoverIdx === null && <circle cx={lastX} cy={lastY} r="2.2" fill="white" />}
        {/* Hover guideline + dot */}
        {hoverIdx !== null && (
          <>
            <line x1={hx} y1={0} x2={hx} y2={height} stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeDasharray="2 2" />
            <circle cx={hx} cy={hy} r="3" fill="white" stroke="rgba(0,0,0,0.25)" strokeWidth="0.5" />
          </>
        )}
      </svg>
      {tooltipText && (
        <div
          className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded bg-black/80 text-white text-[10px] font-semibold whitespace-nowrap pointer-events-none shadow-lg"
          data-testid={testId ? `${testId}-tooltip` : undefined}
        >
          {tooltipText}
        </div>
      )}
    </div>
  );
};

// Accounting Section Component
const AccountingSection = ({ onUpdate }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [recalculating, setRecalculating] = useState(false);
  const [accountingData, setAccountingData] = useState({
    revenue: 0,
    expenses: 0,
    profit: 0,
    pendingPayments: 0,
    previous: null  // when compare_previous=true, holds {total_revenue, total_expenses, net_profit, revenue_change_pct, ...}
  });
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('this_month');
  const [showPLReport, setShowPLReport] = useState(false);
  const [showGSTReport, setShowGSTReport] = useState(false);
  const [showAddExpenseDialog, setShowAddExpenseDialog] = useState(false);
  const [showAddIncomeDialog, setShowAddIncomeDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Credit Management state
  const [creditAccounts, setCreditAccounts] = useState([]);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [creditStats, setCreditStats] = useState({
    totalReceivables: 0,
    totalPayables: 0,
    overdueReceivables: 0,
    overduePayables: 0
  });
  const [showAddCreditAccountDialog, setShowAddCreditAccountDialog] = useState(false);
  const [showEditCreditAccountDialog, setShowEditCreditAccountDialog] = useState(false);
  const [editingCreditAccount, setEditingCreditAccount] = useState(null);
  const [showCreditTransactionDialog, setShowCreditTransactionDialog] = useState(false);
  const [selectedCreditAccount, setSelectedCreditAccount] = useState(null);
  const [creditTransactionType, setCreditTransactionType] = useState('receivable'); // receivable or payable
  const [showSupplierStatementDialog, setShowSupplierStatementDialog] = useState(false);
  const [supplierStatementData, setSupplierStatementData] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [supplierPaymentAccount, setSupplierPaymentAccount] = useState(null);
  const [customerPaymentAccount, setCustomerPaymentAccount] = useState(null);
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [statementData, setStatementData] = useState(null);
  const [showCustomerStatementDialog, setShowCustomerStatementDialog] = useState(false);
  const [customerStatementData, setCustomerStatementData] = useState(null);
  const [statementStart, setStatementStart] = useState(() => {
    const d = new Date();
    return toLocalISODate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [statementEnd, setStatementEnd] = useState(() => toLocalISODate(new Date()));
  
  // Financial Reports state
  const [reportsTab, setReportsTab] = useState('profit-loss');
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [comparePrevious, setComparePrevious] = useState(false);
  const [profitLossData, setProfitLossData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  
  // React Hook Form for Add Expense
  const { 
    register: registerExpense, 
    handleSubmit: handleSubmitExpense, 
    reset: resetExpense, 
    control: controlExpense,
    formState: { errors: expenseErrors, isSubmitting: isExpenseSubmitting }
  } = useForm({
    defaultValues: {
      description: '',
      amount: '',
      category: 'operational',
      date: new Date().toISOString().split('T')[0]
    }
  });

  // React Hook Form for Add Income  
  const { 
    register: registerIncome, 
    handleSubmit: handleSubmitIncome, 
    reset: resetIncome, 
    control: controlIncome,
    watch: watchIncome,
    formState: { errors: incomeErrors, isSubmitting: isIncomeSubmitting }
  } = useForm({
    defaultValues: {
      description: '',
      amount: '',
      category: 'service_revenue',
      date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      bank_account_id: ''
    }
  });
  const incomePaymentMethod = watchIncome('payment_method');
  
  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showAddAccountDialog, setShowAddAccountDialog] = useState(false);
  const [showEditAccountDialog, setShowEditAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  
  // Bank clearing issues state
  const [clearingIssues, setClearingIssues] = useState([]);
  const [showAddClearingIssueDialog, setShowAddClearingIssueDialog] = useState(false);
  const [showEditClearingIssueDialog, setShowEditClearingIssueDialog] = useState(false);
  const [editingClearingIssue, setEditingClearingIssue] = useState(null);
  const [clearingIssuesStats, setClearingIssuesStats] = useState({
    total_issues: 0,
    pending_issues: 0,
    in_progress_issues: 0,
    resolved_issues: 0,
    total_amount: 0,
    pending_amount: 0,
    high_priority: 0,
    critical_priority: 0
  });
  const [selectedAccountForIssues, setSelectedAccountForIssues] = useState('all');
  const [bankAccountsSubTab, setBankAccountsSubTab] = useState('accounts');
  
  // Pending credit expenses state
  const [pendingCredits, setPendingCredits] = useState([]);
  const [vendorSummary, setVendorSummary] = useState([]);
  const [pendingCreditsStats, setPendingCreditsStats] = useState({
    count: 0,
    total_pending_amount: 0,
    vendor_count: 0,
    overdue_count: 0,
    overdue_amount: 0,
    due_soon_count: 0,
    due_soon_amount: 0
  });
  const [showPayCreditDialog, setShowPayCreditDialog] = useState(false);
  const [showPayVendorDialog, setShowPayVendorDialog] = useState(false);
  const [payingCredit, setPayingCredit] = useState(null);
  const [payingVendor, setPayingVendor] = useState(null);
  const [creditsView, setCreditsView] = useState('vendor'); // 'vendor' or 'individual'
  
  // Cash management state
  const [cashBalance, setCashBalance] = useState(0);
  const [cashTransactions, setCashTransactions] = useState([]);
  const [showCashTransactionDialog, setShowCashTransactionDialog] = useState(false);
  const [showAdjustCashDialog, setShowAdjustCashDialog] = useState(false);
  const [adjustCashForm, setAdjustCashForm] = useState({ new_balance: '', reason: '' });
  const [adjustCashSubmitting, setAdjustCashSubmitting] = useState(false);

  // Bank balance adjustment state
  const [showAdjustBankDialog, setShowAdjustBankDialog] = useState(false);
  const [adjustBankAccount, setAdjustBankAccount] = useState(null);
  const [adjustBankForm, setAdjustBankForm] = useState({ new_balance: '', reason: '' });
  const [adjustBankSubmitting, setAdjustBankSubmitting] = useState(false);

  // Inter-bank transfer state
  const [showBankTransferDialog, setShowBankTransferDialog] = useState(false);
  const [bankTransferForm, setBankTransferForm] = useState({ from_account_id: '', to_account_id: '', amount: '', description: '' });
  const [bankTransferSubmitting, setBankTransferSubmitting] = useState(false);

  // Expense details state
  const [expenseDetails, setExpenseDetails] = useState(null);
  const [expenseDetailsLoading, setExpenseDetailsLoading] = useState(false);
  const [expandedExpenseCategory, setExpandedExpenseCategory] = useState(null);

  // Revenue details state
  const [revenueDetails, setRevenueDetails] = useState(null);
  const [revenueDetailsLoading, setRevenueDetailsLoading] = useState(false);
  const [expandedRevenueCategory, setExpandedRevenueCategory] = useState(null);

  // Compare to previous period toggle
  const [comparePreviousPeriod, setComparePreviousPeriod] = useState(false);

  // Trend data for sparkline charts on cards: { buckets: [{date, revenue, expenses, profit}], ... }
  const [trendData, setTrendData] = useState(null);

  // Parts profit data: { total: {revenue, cost, profit, margin_pct, units_sold}, parts: [...], previous: {...} }
  const [partsProfitData, setPartsProfitData] = useState(null);
  const [partsProfitLoading, setPartsProfitLoading] = useState(false);
  const [showAllParts, setShowAllParts] = useState(false);

  // Service revenue data: { total: {revenue, units_performed, invoice_count}, services: [...], invoices: [...], previous: {...} }
  const [serviceRevenueData, setServiceRevenueData] = useState(null);
  const [serviceRevenueLoading, setServiceRevenueLoading] = useState(false);
  const [showAllServiceInvoices, setShowAllServiceInvoices] = useState(false);

  // Compute actual ISO date range from preset (uses existing dateRange + customStartDate/customEndDate state)
  const getDateRange = () => {
    const today = new Date();
    const fmt = toLocalISODate;
    if (dateRange === 'all') return { start: '', end: '' };
    if (dateRange === 'today') return { start: fmt(today), end: fmt(today) };
    if (dateRange === 'this_week') {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return { start: fmt(start), end: fmt(today) };
    }
    if (dateRange === 'this_month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: fmt(start), end: fmt(today) };
    }
    if (dateRange === 'this_year') {
      const start = new Date(today.getFullYear(), 0, 1);
      return { start: fmt(start), end: fmt(today) };
    }
    if (dateRange === 'custom') return { start: customStartDate || '', end: customEndDate || '' };
    return { start: '', end: '' };
  };

  // Build query string for date filter
  const buildDateQuery = (includeCompare = false) => {
    const { start, end } = getDateRange();
    const params = new URLSearchParams();
    if (start) params.append('start_date', start);
    if (end) params.append('end_date', end);
    if (includeCompare && comparePreviousPeriod && start && end) {
      params.append('compare_previous', 'true');
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };
  
  const expenseCategories = [
    { value: 'parts', label: 'Parts Purchase' },
    { value: 'operational', label: 'Operational' },
    { value: 'employee', label: 'Employee Costs' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'rent', label: 'Rent & Facilities' },
    { value: 'maintenance', label: 'Equipment Maintenance' },
    { value: 'office_supplies', label: 'Office Supplies' },
    { value: 'software', label: 'Software' },
    { value: 'tools', label: 'Tools' },
    { value: 'other', label: 'Other' }
  ];

  const incomeCategories = [
    { value: 'service_revenue', label: 'Service Revenue' },
    { value: 'parts_sales', label: 'Parts Sales' },
    { value: 'consultation', label: 'Consultation Fees' },
    { value: 'diagnostic', label: 'Diagnostic Services' },
    { value: 'maintenance', label: 'Maintenance Contracts' },
    { value: 'other', label: 'Other Revenue' }
  ];

  const onSubmitExpense = async (data) => {
    try {
      const expenseData = {
        description: data.description,
        amount: parseFloat(data.amount),
        type: 'expense',
        category: data.category,
        transaction_date: data.date
      };

      // Save to backend
      const response = await axios.post(`${backendUrl}/api/accounting/transactions`, expenseData);
      
      // Add to local transactions
      setTransactions(prev => [response.data, ...prev]);
      
      // Update accounting data
      setAccountingData(prev => ({
        ...prev,
        expenses: prev.expenses + expenseData.amount,
        profit: prev.profit - expenseData.amount
      }));

      toast.success('Expense added successfully');
      setShowAddExpenseDialog(false);
      resetExpense();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const onSubmitIncome = async (data) => {
    if (data.payment_method === 'bank_account' && !data.bank_account_id) {
      toast.error('Please select a bank account to credit this income to');
      return;
    }
    try {
      const incomeData = {
        description: data.description,
        amount: parseFloat(data.amount),
        type: 'income',
        category: data.category,
        transaction_date: data.date,
        payment_method: data.payment_method,
        bank_account_id: data.payment_method === 'bank_account' ? data.bank_account_id : null
      };

      // Save to backend
      const response = await axios.post(`${backendUrl}/api/accounting/transactions`, incomeData);
      
      // Add to local transactions
      setTransactions(prev => [response.data, ...prev]);
      
      // Update accounting summary
      setAccountingData(prev => ({
        ...prev,
        revenue: prev.revenue + incomeData.amount,
        profit: prev.profit + incomeData.amount
      }));

      // Refresh cash/bank balances so the credited amount reflects immediately
      await Promise.all([loadCashData(), loadBankAccounts()]);

      toast.success(
        data.payment_method === 'bank_account'
          ? 'Income added and credited to bank account'
          : 'Income added and credited to cash'
      );
      setShowAddIncomeDialog(false);
      resetIncome();
    } catch (error) {
      console.error('Error adding income:', error);
      toast.error('Failed to add income');
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    const loadAccountingData = async () => {
      setLoading(true);
      try {
        // Fetch real data from APIs
        const qs = buildDateQuery(true);
        const [summaryResponse, transactionsResponse] = await Promise.all([
          axios.get(`${backendUrl}/api/accounting/summary${qs}`),
          axios.get(`${backendUrl}/api/accounting/transactions`)
        ]);

        const summaryData = summaryResponse.data;
        const transactionsData = transactionsResponse.data || [];
        
        setAccountingData({
          revenue: summaryData.total_revenue || 0,
          expenses: summaryData.total_expenses || 0,
          profit: summaryData.net_profit || 0,
          grossProfit: summaryData.gross_profit || 0,
          totalPurchases: summaryData.total_purchases || 0,
          partsConsumed: summaryData.parts_consumed || 0,
          otherExpenses: summaryData.other_expenses || 0,
          partsRevenue: summaryData.parts_revenue || 0,
          labourRevenue: summaryData.labour_revenue || 0,
          supplierOutstanding: summaryData.supplier_outstanding || 0,
          customerOutstanding: summaryData.customer_outstanding || 0,
          purchasePaymentsMade: summaryData.purchase_payments_made || 0,
          pendingPayments: summaryData.pending_payments || 0,
          previous: summaryData.previous || null
        });

        // Get invoices and parts for additional display
        try {
          const [invoicesResponse, partsResponse] = await Promise.all([
            axios.get(`${backendUrl}/api/invoices`),
            axios.get(`${backendUrl}/api/parts`)
          ]);
          setInvoices(invoicesResponse.data || []);
          setParts(partsResponse.data || []);
        } catch (error) {
          console.log('Could not load invoices/parts for display');
        }

        // Sort transactions by date, newest first
        const sortedTransactions = transactionsData.sort((a, b) => 
          new Date(b.transaction_date || b.created_at) - new Date(a.transaction_date || a.created_at)
        );
        
        setTransactions(sortedTransactions.slice(0, 10)); // Show only latest 10
        
      } catch (error) {
        console.error('Error loading accounting data:', error);
        toast.error('Failed to load accounting data');
        
        // Fallback to default values
        setAccountingData({
          revenue: 0,
          expenses: 0,
          profit: 0,
          pendingPayments: 0
        });
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    loadAccountingData();
  }, [dateRange, customStartDate, customEndDate, comparePreviousPeriod, backendUrl]);

  // Load bank accounts
  const loadBankAccounts = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/bank-accounts`);
      setBankAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      toast.error('Failed to load bank accounts');
    }
  };

  // Load cash balance and transactions
  const loadCashData = async () => {
    try {
      const [balanceResponse, transactionsResponse] = await Promise.all([
        axios.get(`${backendUrl}/api/cash/balance`),
        axios.get(`${backendUrl}/api/cash/transactions`)
      ]);
      
      setCashBalance(balanceResponse.data.current_balance || 0);
      setCashTransactions(transactionsResponse.data || []);
    } catch (error) {
      console.error('Error loading cash data:', error);
      toast.error('Failed to load cash data');
    }
  };

  // Load expense breakdown details
  const loadExpenseDetails = async () => {
    setExpenseDetailsLoading(true);
    try {
      const qs = buildDateQuery();
      const response = await axios.get(`${backendUrl}/api/accounting/expense-details${qs}`);
      setExpenseDetails(response.data);
    } catch (error) {
      console.error('Error loading expense details:', error);
    } finally {
      setExpenseDetailsLoading(false);
    }
  };

  // Load revenue breakdown details
  const loadRevenueDetails = async () => {
    setRevenueDetailsLoading(true);
    try {
      const qs = buildDateQuery();
      const response = await axios.get(`${backendUrl}/api/accounting/revenue-details${qs}`);
      setRevenueDetails(response.data);
    } catch (error) {
      console.error('Error loading revenue details:', error);
    } finally {
      setRevenueDetailsLoading(false);
    }
  };

  // Load per-day trend data for the sparkline charts on cards
  const loadTrendData = async () => {
    try {
      const qs = buildDateQuery();
      const response = await axios.get(`${backendUrl}/api/accounting/trend${qs}`);
      setTrendData(response.data);
    } catch (error) {
      console.error('Error loading trend data:', error);
      setTrendData(null);
    }
  };

  // Load parts profit aggregate (paid invoices: selling price - cost price per part)
  const loadPartsProfit = async () => {
    setPartsProfitLoading(true);
    try {
      const qs = buildDateQuery(true);
      const response = await axios.get(`${backendUrl}/api/accounting/parts-profit${qs}`);
      setPartsProfitData(response.data);
    } catch (error) {
      console.error('Error loading parts profit:', error);
      setPartsProfitData(null);
    } finally {
      setPartsProfitLoading(false);
    }
  };

  const handleRecalculateSnapshots = async () => {
    setRecalculating(true);
    try {
      const res = await axios.post(`${backendUrl}/api/accounting/recalculate-cost-snapshots`);
      const { invoices_updated, items_updated } = res.data;
      toast.success(items_updated > 0
        ? `Fixed ${items_updated} line item(s) across ${invoices_updated} invoice(s)`
        : "All historical margins were already correct");
      loadPartsProfit();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to recalculate margins");
    } finally {
      setRecalculating(false);
    }
  };

  // Load service revenue breakdown (paid invoices: service-type line items)
  const loadServiceRevenue = async () => {
    setServiceRevenueLoading(true);
    try {
      const qs = buildDateQuery(true);
      const response = await axios.get(`${backendUrl}/api/accounting/service-revenue${qs}`);
      setServiceRevenueData(response.data);
    } catch (error) {
      console.error('Error loading service revenue:', error);
      setServiceRevenueData(null);
    } finally {
      setServiceRevenueLoading(false);
    }
  };

  // Load additional data based on active tab
  useEffect(() => {
    if (activeTab === 'bank-accounts') {
      loadBankAccounts();
      // Load clearing issues data separately
      loadClearingIssues();
      loadClearingIssuesStats();
      loadPendingCredits();
    } else if (activeTab === 'cash') {
      loadCashData();
      loadBankAccounts(); // needed for bank deposit/withdrawal account dropdown
    } else if (activeTab === 'credit-management') {
      loadCreditAccounts();
      loadCreditTransactions();
    } else if (activeTab === 'dashboard') {
      loadExpenseDetails();
      loadRevenueDetails();
      loadTrendData();
      loadPartsProfit();
      loadServiceRevenue();
    }
  }, [activeTab, backendUrl, dateRange, customStartDate, customEndDate, comparePreviousPeriod]); // refetch when date filter or comparison toggle changes

  // Load credit accounts
  const loadCreditAccounts = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/credit-accounts`);
      setCreditAccounts(response.data || []);
      
      // Calculate statistics
      const accounts = response.data || [];
      const stats = {
        totalReceivables: accounts
          .filter(acc => acc.type === 'customer')
          .reduce((sum, acc) => sum + (acc.outstanding_amount || 0), 0),
        totalPayables: accounts
          .filter(acc => acc.type === 'vendor')
          .reduce((sum, acc) => sum + (acc.outstanding_amount || 0), 0),
        overdueReceivables: 0, // Will be calculated from transactions
        overduePayables: 0  // Will be calculated from transactions
      };
      setCreditStats(stats);
    } catch (error) {
      console.error('Error loading credit accounts:', error);
      toast.error('Failed to load credit accounts');
      setCreditAccounts([]);
    }
  };

  // Load credit transactions  
  const loadCreditTransactions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/credit-transactions`);
      setCreditTransactions(response.data || []);
    } catch (error) {
      console.error('Error loading credit transactions:', error);
      toast.error('Failed to load credit transactions');
      setCreditTransactions([]);
    }
  };

  // Handle add credit account — the form has already POSTed and passes back the created
  // account, so we only update local state here (avoids creating a duplicate record).
  const handleAddCreditAccount = (createdAccount) => {
    if (createdAccount) {
      setCreditAccounts(prev => [...prev, createdAccount]);
    }
    setShowAddCreditAccountDialog(false);
    loadCreditAccounts(); // Reload to update stats
  };

  // Handle delete (deactivate) a credit account
  const handleDeleteCreditAccount = async (account) => {
    if (!window.confirm(`Delete credit account "${account.name}"?\n\nThis removes it from Credit Management. This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${backendUrl}/api/credit-accounts/${account.id}`);
      setCreditAccounts(prev => prev.filter(a => a.id !== account.id));
      toast.success(`Credit account "${account.name}" deleted`);
      loadCreditAccounts();
    } catch (error) {
      console.error('Error deleting credit account:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete credit account');
    }
  };

  // Handle update credit account (e.g. fix a supplier mistakenly saved as Customer)
  const handleUpdateCreditAccount = (updated) => {
    setCreditAccounts(prev => prev.map(a => (a.id === updated.id ? updated : a)));
    setShowEditCreditAccountDialog(false);
    setEditingCreditAccount(null);
    loadCreditAccounts();
  };

  // Handle add credit transaction
  const handleAddCreditTransaction = async (transactionData) => {
    try {
      const response = await axios.post(`${backendUrl}/api/credit-transactions`, transactionData);
      setCreditTransactions(prev => [response.data, ...prev]);
      toast.success('Credit transaction added successfully');
      setShowCreditTransactionDialog(false);
      setSelectedCreditAccount(null);
      loadCreditAccounts(); // Reload accounts to update outstanding amounts
      loadCreditTransactions(); // Reload transactions
    } catch (error) {
      console.error('Error adding credit transaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to add credit transaction');
    }
  };

  // Handle view supplier statement
  const handleViewSupplierStatement = async (account, start = statementStart, end = statementEnd, billType = 'all') => {
    if (!account) return;
    setLoadingStatement(true);
    try {
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      if (billType && billType !== 'all') params.append('bill_type', billType);
      const response = await axios.get(`${backendUrl}/api/credit-accounts/${account.id}/statement?${params.toString()}`);
      setSupplierStatementData(response.data);
      setSelectedCreditAccount(account);
      setStatementStart(start);
      setStatementEnd(end);
      setShowSupplierStatementDialog(true);
    } catch (error) {
      console.error('Error loading supplier statement:', error);
      toast.error('Failed to load supplier statement');
    } finally {
      setLoadingStatement(false);
    }
  };

  // Handle view customer statement
  const handleViewCustomerStatement = async (account, start = statementStart, end = statementEnd) => {
    if (!account) return;
    setLoadingStatement(true);
    try {
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      const response = await axios.get(`${backendUrl}/api/credit-accounts/${account.id}/customer-statement?${params.toString()}`);
      setCustomerStatementData(response.data);
      setSelectedCreditAccount(account);
      setStatementStart(start);
      setStatementEnd(end);
      setShowCustomerStatementDialog(true);
    } catch (error) {
      console.error('Error loading customer statement:', error);
      toast.error('Failed to load customer statement');
    } finally {
      setLoadingStatement(false);
    }
  };

  // Handle add bank account
  const handleAddBankAccount = async (accountData) => {
    try {
      const response = await axios.post(`${backendUrl}/api/bank-accounts`, accountData);
      setBankAccounts(prev => [...prev, response.data]);
      toast.success('Bank account added successfully');
      setShowAddAccountDialog(false);
    } catch (error) {
      console.error('Error adding bank account:', error);
      toast.error(error.response?.data?.detail || 'Failed to add bank account');
    }
  };

  // Handle edit bank account
  const handleEditBankAccount = async (accountId, accountData) => {
    try {
      const response = await axios.put(`${backendUrl}/api/bank-accounts/${accountId}`, accountData);
      setBankAccounts(prev => prev.map(acc => acc.id === accountId ? response.data : acc));
      toast.success('Bank account updated successfully');
      setShowEditAccountDialog(false);
      setEditingAccount(null);
    } catch (error) {
      console.error('Error updating bank account:', error);
      toast.error('Failed to update bank account');
    }
  };

  // Handle delete bank account
  const handleDeleteBankAccount = async (accountId, accountNumber) => {
    if (window.confirm(`Are you sure you want to delete account ${accountNumber}?`)) {
      try {
        await axios.delete(`${backendUrl}/api/bank-accounts/${accountId}`);
        setBankAccounts(prev => prev.filter(acc => acc.id !== accountId));
        toast.success('Bank account deleted successfully');
      } catch (error) {
        console.error('Error deleting bank account:', error);
        toast.error('Failed to delete bank account');
      }
    }
  };

  // ============ Bank Clearing Issues Functions (React Hooks) ============

  // Load clearing issues
  const loadClearingIssues = useCallback(async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/clearing-issues`);
      setClearingIssues(response.data || []);
    } catch (error) {
      console.error('Error loading clearing issues:', error);
      toast.error('Failed to load clearing issues');
    }
  }, [backendUrl]);

  // Load clearing issues stats
  const loadClearingIssuesStats = useCallback(async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/clearing-issues/stats`);
      setClearingIssuesStats(response.data);
    } catch (error) {
      console.error('Error loading clearing issues stats:', error);
      toast.error('Failed to load clearing issues stats');
    }
  }, [backendUrl]);

  // Load clearing issues for specific account
  const loadClearingIssuesForAccount = useCallback(async (accountId) => {
    try {
      if (accountId === 'all') {
        loadClearingIssues();
      } else {
        const response = await axios.get(`${backendUrl}/api/bank-accounts/${accountId}/clearing-issues`);
        setClearingIssues(response.data || []);
      }
    } catch (error) {
      console.error('Error loading clearing issues for account:', error);
      toast.error('Failed to load clearing issues for account');
    }
  }, [backendUrl, loadClearingIssues]);

  // Handle add clearing issue
  const handleAddClearingIssue = async (issueData) => {
    try {
      const response = await axios.post(`${backendUrl}/api/clearing-issues`, issueData);
      setClearingIssues(prev => [response.data, ...prev]);
      toast.success('Clearing issue added successfully');
      setShowAddClearingIssueDialog(false);
      loadClearingIssuesStats(); // Refresh stats
    } catch (error) {
      console.error('Error adding clearing issue:', error);
      toast.error(error.response?.data?.detail || 'Failed to add clearing issue');
    }
  };

  // Handle edit clearing issue
  const handleEditClearingIssue = async (issueId, issueData) => {
    try {
      const response = await axios.put(`${backendUrl}/api/clearing-issues/${issueId}`, issueData);
      setClearingIssues(prev => prev.map(issue => issue.id === issueId ? response.data : issue));
      toast.success('Clearing issue updated successfully');
      setShowEditClearingIssueDialog(false);
      setEditingClearingIssue(null);
      loadClearingIssuesStats(); // Refresh stats
    } catch (error) {
      console.error('Error updating clearing issue:', error);
      toast.error('Failed to update clearing issue');
    }
  };

  // Handle delete clearing issue
  const handleDeleteClearingIssue = async (issueId, issueTitle) => {
    if (window.confirm(`Are you sure you want to delete clearing issue "${issueTitle}"?`)) {
      try {
        await axios.delete(`${backendUrl}/api/clearing-issues/${issueId}`);
        setClearingIssues(prev => prev.filter(issue => issue.id !== issueId));
        toast.success('Clearing issue deleted successfully');
        loadClearingIssuesStats(); // Refresh stats
      } catch (error) {
        console.error('Error deleting clearing issue:', error);
        toast.error('Failed to delete clearing issue');
      }
    }
  };

  // Handle resolve clearing issue (quick action)
  const handleResolveClearingIssue = async (issueId) => {
    try {
      const updateData = {
        status: 'resolved',
        actual_resolution_date: new Date().toISOString().split('T')[0]
      };
      const response = await axios.put(`${backendUrl}/api/clearing-issues/${issueId}`, updateData);
      setClearingIssues(prev => prev.map(issue => issue.id === issueId ? response.data : issue));
      toast.success('Clearing issue marked as resolved');
      loadClearingIssuesStats(); // Refresh stats
    } catch (error) {
      console.error('Error resolving clearing issue:', error);
      toast.error('Failed to resolve clearing issue');
    }
  };

  // Filter clearing issues based on selected account
  const filteredClearingIssues = useMemo(() => {
    if (selectedAccountForIssues === 'all') {
      return clearingIssues;
    }
    return clearingIssues.filter(issue => issue.bank_account_id === selectedAccountForIssues);
  }, [clearingIssues, selectedAccountForIssues]);

  // ============ Pending Credit Expenses Functions ============

  // Load pending credit expenses
  const loadPendingCredits = useCallback(async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/accounting/transactions/pending-credits`);
      setPendingCredits(response.data.pending_credits || []);
      setVendorSummary(response.data.vendor_summary || []);
      setPendingCreditsStats({
        count: response.data.count || 0,
        total_pending_amount: response.data.total_pending_amount || 0,
        vendor_count: response.data.vendor_count || 0,
        overdue_count: response.data.overdue_count || 0,
        overdue_amount: response.data.overdue_amount || 0,
        due_soon_count: response.data.due_soon_count || 0,
        due_soon_amount: response.data.due_soon_amount || 0
      });
    } catch (error) {
      console.error('Error loading pending credits:', error);
      toast.error('Failed to load pending credit expenses');
    }
  }, [backendUrl]);

  // Handle pay credit expense
  const handlePayCredit = async (transaction, paymentData) => {
    try {
      await axios.put(`${backendUrl}/api/accounting/transactions/${transaction.id}/pay-credit`, paymentData);
      toast.success(`Credit expense of ₹${transaction.amount?.toLocaleString()} paid successfully!`);
      
      // Refresh pending credits and balances
      loadPendingCredits();
      if (activeTab === 'bank-accounts') {
        loadBankAccounts();
        loadCashData();
      }
      
      setShowPayCreditDialog(false);
      setPayingCredit(null);
    } catch (error) {
      console.error('Error paying credit expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to pay credit expense');
    }
  };

  // Handle pay vendor (all credits for a vendor)
  const handlePayVendor = async (vendor, paymentData) => {
    try {
      const response = await axios.put(`${backendUrl}/api/accounting/vendors/${encodeURIComponent(vendor.vendor_name)}/pay-all-credits`, paymentData);
      toast.success(`₹${paymentData.amount?.toLocaleString()} paid successfully to ${vendor.vendor_name}!`);
      
      // Refresh pending credits and balances
      loadPendingCredits();
      if (activeTab === 'bank-accounts') {
        loadBankAccounts();
        loadCashData();
      }
      
      setShowPayVendorDialog(false);
      setPayingVendor(null);
    } catch (error) {
      console.error('Error paying vendor:', error);
      toast.error(error.response?.data?.detail || 'Failed to pay vendor');
    }
  };

  // Handle delete pending credit
  const handleDeletePendingCredit = async (transactionId, description) => {
    if (window.confirm(`Are you sure you want to delete the pending credit expense "${description}"?`)) {
      try {
        await axios.delete(`${backendUrl}/api/accounting/transactions/${transactionId}`);
        setPendingCredits(prev => prev.filter(credit => credit.id !== transactionId));
        loadPendingCredits(); // Refresh stats
        toast.success('Pending credit expense deleted successfully');
      } catch (error) {
        console.error('Error deleting pending credit:', error);
        toast.error('Failed to delete pending credit expense');
      }
    }
  };

  // Handle cash transaction
  const handleCashTransaction = async (transactionData) => {
    try {
      const response = await axios.post(`${backendUrl}/api/cash/transactions`, transactionData);
      setCashTransactions(prev => [response.data, ...prev]);
      setCashBalance(response.data.balance_after);
      toast.success('Cash transaction recorded successfully');
      setShowCashTransactionDialog(false);
      
      // If bank transaction, reload bank accounts
      if (transactionData.bank_account_id) {
        loadBankAccounts();
      }
    } catch (error) {
      console.error('Error adding cash transaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to record transaction');
    }
  };

  // Render Financial Dashboard Tab
  const renderFinancialDashboard = () => (
    <div className="space-y-6 mt-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Financial Overview</h2>
        <div className="flex space-x-2">
          <Button 
            className="bg-red-600 hover:bg-red-700"
            onClick={() => setShowAddExpenseDialog(true)}
          >
            <Minus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setShowAddIncomeDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Income
          </Button>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 opacity-80" />
                <div className="ml-4">
                  <p className="text-sm opacity-90">Total Revenue</p>
                  <p className="text-2xl font-bold">₹{accountingData.revenue.toLocaleString()}</p>
                  {accountingData.previous && (
                    <ComparisonBadge
                      pct={accountingData.previous.revenue_change_pct}
                      prevValue={accountingData.previous.total_revenue}
                      higherIsBetter={true}
                    />
                  )}
                </div>
              </div>
              {trendData?.buckets?.length > 1 && (
                <Sparkline
                  values={trendData.buckets.map(b => b.revenue)}
                  labels={trendData.buckets.map(b => b.date)}
                  testId="sparkline-revenue"
                />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center">
                <Calculator className="h-8 w-8 opacity-80" />
                <div className="ml-4">
                  <p className="text-sm opacity-90">Total Expenses</p>
                  <p className="text-2xl font-bold">₹{accountingData.expenses.toLocaleString()}</p>
                  <p className="text-[10px] opacity-70">Salaries + Purchase Order Payments + Garage Expenses</p>
                  {accountingData.previous && (
                    <ComparisonBadge
                      pct={accountingData.previous.expenses_change_pct}
                      prevValue={accountingData.previous.total_expenses}
                      higherIsBetter={false}
                    />
                  )}
                </div>
              </div>
              {trendData?.buckets?.length > 1 && (
                <Sparkline
                  values={trendData.buckets.map(b => b.expenses)}
                  labels={trendData.buckets.map(b => b.date)}
                  testId="sparkline-expenses"
                />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-gradient-to-r ${accountingData.profit >= 0 ? 'from-blue-500 to-blue-600' : 'from-red-500 to-red-600'} text-white`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center">
                <BarChart3 className="h-8 w-8 opacity-80" />
                <div className="ml-4">
                  <p className="text-sm opacity-90">Net Profit</p>
                  <p className="text-2xl font-bold">₹{accountingData.profit.toLocaleString()}</p>
                  {accountingData.previous && (
                    <ComparisonBadge
                      pct={accountingData.previous.profit_change_pct}
                      prevValue={accountingData.previous.net_profit}
                      higherIsBetter={true}
                    />
                  )}
                </div>
              </div>
              {trendData?.buckets?.length > 1 && (
                <Sparkline
                  values={trendData.buckets.map(b => b.profit)}
                  labels={trendData.buckets.map(b => b.date)}
                  testId="sparkline-profit"
                />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 opacity-80" />
              <div className="ml-4">
                <p className="text-sm opacity-90">Pending Payments</p>
                <p className="text-2xl font-bold">₹{accountingData.pendingPayments.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all"
              onClick={() => {
                setActiveTab('bank-accounts');
                setBankAccountsSubTab('pending-credits');
              }}>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CreditCard className="h-8 w-8 opacity-80" />
              <div className="ml-4">
                <p className="text-sm opacity-90">Credit Purchases</p>
                <p className="text-2xl font-bold">₹{pendingCreditsStats.total_pending_amount?.toLocaleString()}</p>
                {pendingCreditsStats.count > 0 && (
                  <p className="text-xs opacity-75">{pendingCreditsStats.count} pending</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accrual-basis Accounting Cards: Parts/Labour split, Purchases, Outstanding, Gross Profit */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="accounting-accrual-cards">
        <Card className="border-purple-200" data-testid="card-parts-revenue">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-4 w-4 text-purple-600" />
              <p className="text-xs font-medium text-gray-600">Parts Revenue</p>
            </div>
            <p className="text-xl font-bold text-purple-700">₹{(accountingData.partsRevenue || 0).toLocaleString()}</p>
            <p className="text-[10px] text-gray-500 mt-1">From paid invoices</p>
          </CardContent>
        </Card>

        <Card className="border-blue-200" data-testid="card-labour-revenue">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wrench className="h-4 w-4 text-blue-600" />
              <p className="text-xs font-medium text-gray-600">Labour Revenue</p>
            </div>
            <p className="text-xl font-bold text-blue-700">₹{(accountingData.labourRevenue || 0).toLocaleString()}</p>
            <p className="text-[10px] text-gray-500 mt-1">From paid invoices</p>
          </CardContent>
        </Card>

        <Card className="border-orange-200" data-testid="card-total-purchases">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Truck className="h-4 w-4 text-orange-600" />
              <p className="text-xs font-medium text-gray-600">Total Purchases</p>
            </div>
            <p className="text-xl font-bold text-orange-700">₹{(accountingData.totalPurchases || 0).toLocaleString()}</p>
            <p className="text-[10px] text-gray-500 mt-1">Delivered POs — stock committed, not an expense</p>
          </CardContent>
        </Card>

        <Card className="border-teal-200" data-testid="card-parts-consumed">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-4 w-4 text-teal-600" />
              <p className="text-xs font-medium text-gray-600">Parts Consumed (COGS)</p>
            </div>
            <p className="text-xl font-bold text-teal-700">₹{(accountingData.partsConsumed || 0).toLocaleString()}</p>
            <p className="text-[10px] text-gray-500 mt-1">Cost of parts sold — informational (margin insight), not in Net Profit</p>
          </CardContent>
        </Card>

        <Card className="border-red-200" data-testid="card-supplier-outstanding">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <p className="text-xs font-medium text-gray-600">Supplier Outstanding</p>
            </div>
            <p className="text-xl font-bold text-red-700">₹{(accountingData.supplierOutstanding || 0).toLocaleString()}</p>
            <p className="text-[10px] text-gray-500 mt-1">Accounts Payable</p>
          </CardContent>
        </Card>

        <Card className="border-yellow-200" data-testid="card-customer-outstanding">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-yellow-700" />
              <p className="text-xs font-medium text-gray-600">Customer Outstanding</p>
            </div>
            <p className="text-xl font-bold text-yellow-700">₹{(accountingData.customerOutstanding || 0).toLocaleString()}</p>
            <p className="text-[10px] text-gray-500 mt-1">Accounts Receivable</p>
          </CardContent>
        </Card>

        <Card className={`border-2 ${(accountingData.grossProfit || 0) >= 0 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`} data-testid="card-gross-profit">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className={`h-4 w-4 ${(accountingData.grossProfit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} />
              <p className="text-xs font-medium text-gray-600">Gross Profit</p>
            </div>
            <p className={`text-xl font-bold ${(accountingData.grossProfit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              ₹{(accountingData.grossProfit || 0).toLocaleString()}
            </p>
            <p className="text-[10px] text-gray-500 mt-1">Revenue − COGS (margin insight, not Net Profit)</p>
          </CardContent>
        </Card>
      </div>

      {/* Parts Profitability Section */}
      <Card className="border border-emerald-200" data-testid="parts-profit-section">
        <CardHeader className="flex flex-row items-center justify-between gap-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Package className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <CardTitle className="text-base text-emerald-900">Parts Profitability</CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">Selling price − cost price for parts sold via paid invoices</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRecalculateSnapshots}
                disabled={recalculating}
                data-testid="recalculate-margins-btn"
                title="Backfill correct brand cost on old invoices created before the multi-brand cost fix"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${recalculating ? 'animate-spin' : ''}`} />
                {recalculating ? 'Recalculating...' : 'Recalculate Historical Margins'}
              </Button>
            )}
            {partsProfitData?.total && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Total Parts Profit</p>
              <p className={`text-2xl font-bold ${partsProfitData.total.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="parts-profit-total">
                ₹{partsProfitData.total.profit.toLocaleString()}
              </p>
              <div className="flex items-center justify-end gap-2 text-xs mt-0.5">
                <span className="text-gray-600">Margin: <span className="font-semibold">{partsProfitData.total.margin_pct}%</span></span>
                {partsProfitData.previous?.profit_change_pct !== undefined && partsProfitData.previous.profit_change_pct !== null && (
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      (partsProfitData.previous.profit_change_pct >= 0)
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                    data-testid="parts-profit-comparison"
                  >
                    {partsProfitData.previous.profit_change_pct >= 0 ? '▲' : '▼'} {Math.abs(partsProfitData.previous.profit_change_pct).toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          )}
          </div>
        </CardHeader>
        <CardContent>
          {partsProfitLoading ? (
            <p className="text-sm text-gray-500 text-center py-6">Loading…</p>
          ) : !partsProfitData || partsProfitData.parts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>No parts sold in this period.</p>
              <p className="text-xs mt-1">Mark an invoice as paid with part line items to see profitability here.</p>
            </div>
          ) : (
            <>
              {/* Insights — Best/Worst Performers */}
              {partsProfitData.parts.length > 0 && (() => {
                const parts = partsProfitData.parts;
                // Best & worst margin (only consider parts with revenue > 0)
                const withRevenue = parts.filter(p => p.revenue > 0);
                const bestMargin = withRevenue.length
                  ? withRevenue.reduce((a, b) => (b.margin_pct > a.margin_pct ? b : a))
                  : null;
                const worstMargin = withRevenue.length > 1
                  ? withRevenue.reduce((a, b) => (b.margin_pct < a.margin_pct ? b : a))
                  : null;
                const topSeller = parts.reduce((a, b) => (b.units_sold > a.units_sold ? b : a));
                const topProfit = parts[0]; // already sorted by profit desc

                const InsightCard = ({ icon, label, part, valueText, valueClass, testId }) => (
                  <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-3" data-testid={testId}>
                    <span className="text-xl shrink-0">{icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
                      <p className="text-sm font-semibold text-gray-900 truncate">{part.part_name}</p>
                      <p className={`text-xs font-bold ${valueClass}`}>{valueText}</p>
                    </div>
                  </div>
                );

                return (
                  <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="parts-profit-insights">
                    {topProfit && (
                      <InsightCard
                        icon="💰"
                        label="Top Profit"
                        part={topProfit}
                        valueText={`₹${topProfit.profit.toLocaleString()} profit`}
                        valueClass={topProfit.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}
                        testId="insight-top-profit"
                      />
                    )}
                    {bestMargin && (
                      <InsightCard
                        icon="🏆"
                        label="Best Margin"
                        part={bestMargin}
                        valueText={`${bestMargin.margin_pct}% margin`}
                        valueClass="text-emerald-700"
                        testId="insight-best-margin"
                      />
                    )}
                    {topSeller && (
                      <InsightCard
                        icon="🔥"
                        label="Top Seller"
                        part={topSeller}
                        valueText={`${topSeller.units_sold} units sold`}
                        valueClass="text-blue-700"
                        testId="insight-top-seller"
                      />
                    )}
                    {worstMargin && worstMargin.part_id !== bestMargin?.part_id && (
                      <InsightCard
                        icon="📉"
                        label="Lowest Margin"
                        part={worstMargin}
                        valueText={`${worstMargin.margin_pct}% margin`}
                        valueClass={worstMargin.margin_pct >= 0 ? 'text-amber-700' : 'text-red-600'}
                        testId="insight-worst-margin"
                      />
                    )}
                  </div>
                );
              })()}

              {/* Summary chips */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-500">Units Sold</p>
                  <p className="text-lg font-bold text-gray-800">{partsProfitData.total.units_sold}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-green-600">Revenue</p>
                  <p className="text-lg font-bold text-green-700">₹{partsProfitData.total.revenue.toLocaleString()}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                  <p className="text-xs text-orange-600">Cost (COGS)</p>
                  <p className="text-lg font-bold text-orange-700">₹{partsProfitData.total.cost.toLocaleString()}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <p className="text-xs text-emerald-600">Gross Profit</p>
                  <p className="text-lg font-bold text-emerald-700">₹{partsProfitData.total.profit.toLocaleString()}</p>
                </div>
              </div>

              {/* Per-part breakdown */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="parts-profit-table">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2">Part Name</th>
                      <th className="text-right px-3 py-2">Units Sold</th>
                      <th className="text-right px-3 py-2">Revenue</th>
                      <th className="text-right px-3 py-2">Cost</th>
                      <th className="text-right px-3 py-2">Profit</th>
                      <th className="text-right px-3 py-2">Margin %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(showAllParts ? partsProfitData.parts : partsProfitData.parts.slice(0, 5)).map((p) => (
                      <tr key={p.part_id} className="hover:bg-emerald-50/40" data-testid={`parts-profit-row-${p.part_id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{p.part_name}</div>
                          {p.part_number && <div className="text-xs text-gray-500">{p.part_number}</div>}
                          {Array.isArray(p.matched_pos) && p.matched_pos.length > 0 && (
                            <div
                              className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[220px]"
                              title={p.matched_pos.map(mp => `${mp.po_number}: ${mp.quantity} unit(s) @ ₹${mp.unit_cost}`).join(' | ')}
                              data-testid={`parts-profit-sourced-${p.part_id}`}
                            >
                              Sourced: {p.matched_pos.map(mp => mp.po_number).join(', ')}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{p.units_sold}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-green-700">₹{p.revenue.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-orange-700">₹{p.cost.toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${p.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          ₹{p.profit.toLocaleString()}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums ${p.margin_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {p.margin_pct}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {partsProfitData.parts.length > 5 && (
                <div className="mt-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAllParts(!showAllParts)}
                    data-testid="parts-profit-toggle-all"
                  >
                    {showAllParts ? `Show top 5 only` : `Show all ${partsProfitData.parts.length} parts`}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Service Revenue Section */}
      <Card className="border border-indigo-200" data-testid="service-revenue-section">
        <CardHeader className="flex flex-row items-center justify-between gap-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Wrench className="h-5 w-5 text-indigo-700" />
            </div>
            <div>
              <CardTitle className="text-base text-indigo-900">Service Revenue</CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">Revenue from service line items across paid invoices</p>
            </div>
          </div>
          {serviceRevenueData?.total && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Total Service Revenue</p>
              <p className="text-2xl font-bold text-indigo-700" data-testid="service-revenue-total">
                ₹{serviceRevenueData.total.revenue.toLocaleString()}
              </p>
              {serviceRevenueData.previous?.revenue_change_pct !== undefined && serviceRevenueData.previous.revenue_change_pct !== null && (
                <span
                  className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    serviceRevenueData.previous.revenue_change_pct >= 0
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                  data-testid="service-revenue-comparison"
                >
                  {serviceRevenueData.previous.revenue_change_pct >= 0 ? '▲' : '▼'} {Math.abs(serviceRevenueData.previous.revenue_change_pct).toFixed(1)}%
                </span>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {serviceRevenueLoading ? (
            <p className="text-sm text-gray-500 text-center py-6">Loading…</p>
          ) : !serviceRevenueData || serviceRevenueData.invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <Wrench className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>No services billed in this period.</p>
              <p className="text-xs mt-1">Mark an invoice as paid with service line items to see revenue here.</p>
            </div>
          ) : (
            <>
              {/* Summary chips */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                  <p className="text-xs text-indigo-600">Revenue</p>
                  <p className="text-lg font-bold text-indigo-700">₹{serviceRevenueData.total.revenue.toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-xs text-blue-600">Units Performed</p>
                  <p className="text-lg font-bold text-blue-700">{serviceRevenueData.total.units_performed}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <p className="text-xs text-purple-600">Invoices</p>
                  <p className="text-lg font-bold text-purple-700">{serviceRevenueData.total.invoice_count}</p>
                </div>
              </div>

              {/* Per-service breakdown */}
              {serviceRevenueData.services?.length > 0 && (
                <div className="mb-5">
                  <h4 className="text-xs uppercase tracking-wider text-gray-600 font-semibold mb-2">By Service</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="service-revenue-by-service-table">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2">Service</th>
                          <th className="text-right px-3 py-2">Units Performed</th>
                          <th className="text-right px-3 py-2">Avg Price</th>
                          <th className="text-right px-3 py-2">Total Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {serviceRevenueData.services.map((s, idx) => (
                          <tr key={`${s.name}-${idx}`} className="hover:bg-indigo-50/40">
                            <td className="px-3 py-2 font-medium text-gray-900">{s.name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.units_performed}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-600">₹{s.avg_price.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-indigo-700">₹{s.revenue.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Per-invoice breakdown */}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-gray-600 font-semibold mb-2">By Invoice</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="service-revenue-by-invoice-table">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2">Invoice #</th>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-right px-3 py-2">Services</th>
                        <th className="text-right px-3 py-2">Service Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(showAllServiceInvoices ? serviceRevenueData.invoices : serviceRevenueData.invoices.slice(0, 8)).map((inv) => (
                        <tr key={inv.invoice_id} className="hover:bg-indigo-50/40" data-testid={`service-invoice-row-${inv.invoice_id}`}>
                          <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-900">{inv.customer_name}</div>
                            {inv.vehicle_info && <div className="text-xs text-gray-500 truncate max-w-[180px]">{inv.vehicle_info}</div>}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {inv.date ? new Date(inv.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{inv.service_count}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-indigo-700">₹{inv.service_revenue.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {serviceRevenueData.invoices.length > 8 && (
                  <div className="mt-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllServiceInvoices(!showAllServiceInvoices)}
                      data-testid="service-invoices-toggle-all"
                    >
                      {showAllServiceInvoices ? `Show recent 8 only` : `Show all ${serviceRevenueData.invoices.length} invoices`}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions for Pending Credits */}
      {pendingCreditsStats.count > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Pending Credit Purchases - Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-700">
                  You have {pendingCreditsStats.count} pending credit purchase{pendingCreditsStats.count > 1 ? 's' : ''} 
                  totaling ₹{pendingCreditsStats.total_pending_amount?.toLocaleString()}
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  Click to view and process payments for credit purchases
                </p>
              </div>
              <Button 
                onClick={() => {
                  setActiveTab('bank-accounts');
                  setBankAccountsSubTab('pending-credits');
                }}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Manage Credits ({pendingCreditsStats.count})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Total Revenue Details */}
      <Card data-testid="revenue-details-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Total Revenue Details
            </CardTitle>
            {revenueDetails && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Grand Total</p>
                <p className="text-xl font-bold text-green-600" data-testid="revenue-grand-total">
                  ₹{revenueDetails.grand_total?.toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {revenueDetailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-500">Loading revenue details...</span>
            </div>
          ) : revenueDetails ? (
            <div className="space-y-4">
              {revenueDetails.categories?.length > 0 ? (
                <div className="space-y-3">
                  {revenueDetails.categories.map((cat) => {
                    const percentage = revenueDetails.grand_total > 0 
                      ? ((cat.total / revenueDetails.grand_total) * 100).toFixed(1) 
                      : 0;
                    const isExpanded = expandedRevenueCategory === cat.key;
                    const categoryRevenue = revenueDetails.recent_revenue?.filter(e => e.category === cat.key) || [];
                    
                    const categoryColors = {
                      advance_payment: 'bg-amber-500', service_revenue: 'bg-green-500',
                      paid_invoice: 'bg-blue-500', parts_sales: 'bg-cyan-500',
                      consultation: 'bg-violet-500', diagnostic: 'bg-teal-500',
                      maintenance: 'bg-indigo-500', other: 'bg-gray-500'
                    };
                    const barColor = categoryColors[cat.key] || 'bg-green-500';

                    return (
                      <div key={cat.key} className="border rounded-lg overflow-hidden" data-testid={`revenue-category-${cat.key}`}>
                        <div 
                          className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => setExpandedRevenueCategory(isExpanded ? null : cat.key)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-3 h-3 rounded-full ${barColor}`}></div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{cat.label}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-gray-500">{cat.count} item{cat.count !== 1 ? 's' : ''}</span>
                                  <span className="font-semibold text-sm text-green-600">₹{cat.total.toLocaleString()}</span>
                                  <span className="text-xs text-gray-400 w-12 text-right">{percentage}%</span>
                                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div className={`${barColor} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {isExpanded && categoryRevenue.length > 0 && (
                          <div className="border-t bg-gray-50 divide-y divide-gray-100">
                            {categoryRevenue.slice(0, 20).map((rev, idx) => (
                              <div key={rev.id || idx} className="flex items-center justify-between px-4 py-2 text-sm">
                                <div className="flex-1">
                                  <p className="text-gray-800">{rev.description || 'No description'}</p>
                                  <p className="text-xs text-gray-400">
                                    {rev.date ? new Date(rev.date).toLocaleDateString() : ''}
                                    {rev.customer_name ? ` · ${rev.customer_name}` : ''}
                                    {rev.payment_method ? ` · ${rev.payment_method}` : ''}
                                  </p>
                                </div>
                                <span className="font-medium text-green-600 ml-4">+₹{rev.amount?.toLocaleString()}</span>
                              </div>
                            ))}
                            {categoryRevenue.length > 20 && (
                              <div className="px-4 py-2 text-xs text-gray-400 text-center">
                                +{categoryRevenue.length - 20} more transactions
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No revenue recorded yet</p>
                  <p className="text-sm">Revenue is tracked from paid invoices, advance payments, and income entries</p>
                </div>
              )}

              {revenueDetails.categories?.length > 0 && (
                <div className="flex items-center justify-between pt-3 border-t text-sm text-gray-500">
                  <span>{revenueDetails.total_transactions} total revenue transaction{revenueDetails.total_transactions !== 1 ? 's' : ''}</span>
                  <span>{revenueDetails.categories.length} source{revenueDetails.categories.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <p>Could not load revenue details</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Total Expenses Details */}
      <Card data-testid="expense-details-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-red-600" />
              Total Expenses Details
            </CardTitle>
            {expenseDetails && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Grand Total</p>
                <p className="text-xl font-bold text-red-600" data-testid="expense-grand-total">
                  ₹{expenseDetails.grand_total?.toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {expenseDetailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              <span className="ml-3 text-gray-500">Loading expense details...</span>
            </div>
          ) : expenseDetails ? (
            <div className="space-y-4">
              {/* Category Breakdown */}
              {expenseDetails.categories?.length > 0 ? (
                <div className="space-y-3">
                  {expenseDetails.categories.map((cat) => {
                    const percentage = expenseDetails.grand_total > 0 
                      ? ((cat.total / expenseDetails.grand_total) * 100).toFixed(1) 
                      : 0;
                    const isExpanded = expandedExpenseCategory === cat.key;
                    const categoryExpenses = expenseDetails.recent_expenses?.filter(e => e.category === cat.key) || [];
                    
                    const categoryColors = {
                      parts: 'bg-blue-500', operational: 'bg-orange-500', employee: 'bg-purple-500',
                      utilities: 'bg-yellow-500', rent: 'bg-pink-500', maintenance: 'bg-teal-500',
                      supplies: 'bg-cyan-500', equipment: 'bg-indigo-500', parts_inventory: 'bg-emerald-500',
                      office_supplies: 'bg-lime-500', software: 'bg-violet-500', tools: 'bg-sky-500',
                      other: 'bg-gray-500'
                    };
                    const barColor = categoryColors[cat.key] || 'bg-gray-500';

                    return (
                      <div key={cat.key} className="border rounded-lg overflow-hidden" data-testid={`expense-category-${cat.key}`}>
                        <div 
                          className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => setExpandedExpenseCategory(isExpanded ? null : cat.key)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-3 h-3 rounded-full ${barColor}`}></div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{cat.label}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-gray-500">{cat.count} item{cat.count !== 1 ? 's' : ''}</span>
                                  <span className="font-semibold text-sm text-red-600">₹{cat.total.toLocaleString()}</span>
                                  <span className="text-xs text-gray-400 w-12 text-right">{percentage}%</span>
                                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </div>
                              {/* Progress bar */}
                              <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div className={`${barColor} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Expanded detail rows */}
                        {isExpanded && categoryExpenses.length > 0 && (
                          <div className="border-t bg-gray-50 divide-y divide-gray-100">
                            {categoryExpenses.slice(0, 20).map((expense, idx) => (
                              <div key={expense.id || idx} className="flex items-center justify-between px-4 py-2 text-sm">
                                <div className="flex-1">
                                  <p className="text-gray-800">{expense.description || 'No description'}</p>
                                  <p className="text-xs text-gray-400">
                                    {expense.date ? new Date(expense.date).toLocaleDateString() : ''} 
                                    {expense.payment_method ? ` · ${expense.payment_method}` : ''}
                                  </p>
                                </div>
                                <span className="font-medium text-red-600 ml-4">₹{expense.amount?.toLocaleString()}</span>
                              </div>
                            ))}
                            {categoryExpenses.length > 20 && (
                              <div className="px-4 py-2 text-xs text-gray-400 text-center">
                                +{categoryExpenses.length - 20} more transactions
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Calculator className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No expenses recorded yet</p>
                  <p className="text-sm">Add expenses using the "Add Expense" button above</p>
                </div>
              )}

              {/* Summary footer */}
              {expenseDetails.categories?.length > 0 && (
                <div className="flex items-center justify-between pt-3 border-t text-sm text-gray-500">
                  <span>{expenseDetails.total_transactions} total expense transaction{expenseDetails.total_transactions !== 1 ? 's' : ''}</span>
                  <span>{expenseDetails.categories.length} categor{expenseDetails.categories.length !== 1 ? 'ies' : 'y'}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <p>Could not load expense details</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Net Profit Details */}
      <Card data-testid="profit-details-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className={`h-5 w-5 ${revenueDetails && expenseDetails && (revenueDetails.grand_total - expenseDetails.grand_total) >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
              Net Profit Details
            </CardTitle>
            {revenueDetails && expenseDetails && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Net {(revenueDetails.grand_total - expenseDetails.grand_total) >= 0 ? 'Profit' : 'Loss'}</p>
                <p className={`text-xl font-bold ${(revenueDetails.grand_total - expenseDetails.grand_total) >= 0 ? 'text-blue-600' : 'text-red-600'}`} data-testid="net-profit-total">
                  ₹{Math.abs(revenueDetails.grand_total - expenseDetails.grand_total).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {(revenueDetailsLoading || expenseDetailsLoading) ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-500">Calculating profit details...</span>
            </div>
          ) : (revenueDetails && expenseDetails) ? (() => {
            const totalRevenue = revenueDetails.grand_total || 0;
            const totalExpenses = expenseDetails.grand_total || 0;
            const netProfit = totalRevenue - totalExpenses;
            const isProfit = netProfit >= 0;
            const maxVal = Math.max(totalRevenue, totalExpenses) || 1;
            const revenuePct = ((totalRevenue / maxVal) * 100).toFixed(1);
            const expensePct = ((totalExpenses / maxVal) * 100).toFixed(1);

            return (
              <div className="space-y-6">
                {/* Revenue vs Expenses Visual Comparison */}
                <div className="space-y-4">
                  {/* Revenue bar */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-green-700 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" /> Total Revenue
                      </span>
                      <span className="text-sm font-bold text-green-600">+₹{totalRevenue.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-4">
                      <div className="bg-green-500 h-4 rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                           style={{ width: `${revenuePct}%`, minWidth: '40px' }}>
                        <span className="text-[10px] text-white font-medium">{revenuePct}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Expenses bar */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-red-700 flex items-center gap-2">
                        <TrendingDown className="h-4 w-4" /> Total Expenses
                      </span>
                      <span className="text-sm font-bold text-red-600">-₹{totalExpenses.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-4">
                      <div className="bg-red-500 h-4 rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                           style={{ width: `${expensePct}%`, minWidth: '40px' }}>
                        <span className="text-[10px] text-white font-medium">{expensePct}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Net Profit/Loss Result */}
                <div className={`rounded-lg p-4 ${isProfit ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isProfit ? (
                        <ArrowUpCircle className="h-8 w-8 text-blue-600" />
                      ) : (
                        <ArrowDownCircle className="h-8 w-8 text-red-600" />
                      )}
                      <div>
                        <p className={`text-lg font-bold ${isProfit ? 'text-blue-800' : 'text-red-800'}`}>
                          {isProfit ? 'Net Profit' : 'Net Loss'}
                        </p>
                        <p className={`text-sm ${isProfit ? 'text-blue-600' : 'text-red-600'}`}>
                          {isProfit ? 'Revenue exceeds expenses' : 'Expenses exceed revenue'}
                        </p>
                      </div>
                    </div>
                    <p className={`text-2xl font-bold ${isProfit ? 'text-blue-700' : 'text-red-700'}`}>
                      {isProfit ? '+' : '-'}₹{Math.abs(netProfit).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Revenue Sources vs Expense Categories Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Revenue Sources */}
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" /> Revenue Sources
                    </h4>
                    <div className="space-y-2">
                      {revenueDetails.categories?.map((cat) => (
                        <div key={cat.key} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{cat.label}</span>
                          <span className="font-medium text-green-600">+₹{cat.total.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-sm font-bold pt-2 border-t">
                        <span className="text-gray-800">Total Revenue</span>
                        <span className="text-green-700">₹{totalRevenue.toLocaleString()}</span>
                      </div>
                      {revenueDetails.pending_advances?.total > 0 && (
                        <div className="mt-3 pt-3 border-t border-dashed" data-testid="pending-advances-block">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-amber-700">Advance Payments (Not Yet Invoiced)</span>
                            <span className="font-medium text-amber-600" data-testid="pending-advances-amount">
                              ₹{revenueDetails.pending_advances.total.toLocaleString()}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-500 mt-1">{revenueDetails.pending_advances.note}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expense Categories */}
                  <div className="border rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <Calculator className="h-4 w-4" /> Expense Categories
                    </h4>
                    <div className="space-y-2">
                      {expenseDetails.categories?.slice(0, 5).map((cat) => (
                        <div key={cat.key} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{cat.label}</span>
                          <span className="font-medium text-red-600">-₹{cat.total.toLocaleString()}</span>
                        </div>
                      ))}
                      {expenseDetails.categories?.length > 5 && (
                        <div className="text-xs text-gray-400">+{expenseDetails.categories.length - 5} more categories</div>
                      )}
                      <div className="flex items-center justify-between text-sm font-bold pt-2 border-t">
                        <span className="text-gray-800">Total Expenses</span>
                        <span className="text-red-700">₹{totalExpenses.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profit Margin */}
                {totalRevenue > 0 && (
                  <div className="flex items-center justify-between pt-3 border-t text-sm text-gray-500">
                    <span>Profit Margin: {((netProfit / totalRevenue) * 100).toFixed(1)}%</span>
                    <span>Revenue-to-Expense Ratio: {(totalRevenue / (totalExpenses || 1)).toFixed(2)}x</span>
                  </div>
                )}
              </div>
            );
          })() : (
            <div className="text-center py-6 text-gray-500">
              <p>Could not load profit details</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {transactions.slice(0, 5).map((transaction, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    transaction.type === 'income' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {transaction.type === 'income' ? 
                      <DollarSign className="h-4 w-4 text-green-600" /> : 
                      <Minus className="h-4 w-4 text-red-600" />
                    }
                  </div>
                  <div>
                    <p className="font-medium">{transaction.description}</p>
                    <p className="text-sm text-gray-600">{transaction.customer_name || transaction.supplier_name || ''}</p>
                  </div>
                </div>
                <div className={`font-semibold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                  {transaction.type === 'income' ? '+' : '-'}₹{transaction.amount?.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Bank Accounts Tab
  // View a bank account statement
  const handleViewBankStatement = async (account) => {
    try {
      const res = await axios.get(`${backendUrl}/api/bank-accounts/${account.id}/transactions`);
      const rows = (res.data.transactions || []).map((t) => {
        const raw = t.transaction_date || t.created_at || '';
        return {
          id: t.id,
          sortKey: t.created_at || t.transaction_date || '',
          dateISO: raw.slice(0, 10),
          dateLabel: raw ? new Date(t.transaction_date || t.created_at).toLocaleDateString() : '—',
          description: t.description,
          typeLabel: (t.transaction_type && t.transaction_type !== 'transaction')
            ? t.transaction_type
            : (t.type === 'credit' ? 'deposit' : 'withdrawal'),
          direction: t.type === 'credit' ? 'in' : 'out',
          amount: t.amount,
          balance: t.balance_after,
        };
      });
      setStatementData({
        title: `Bank Statement — ${account.bank_name}`,
        subtitle: `${account.account_type || ''} • A/C ****${(account.account_number || '').slice(-4)}`,
        transactions: rows,
        currentBalance: res.data.current_balance,
      });
      setShowStatementDialog(true);
    } catch (error) {
      console.error('Failed to load bank statement', error);
      toast.error('Failed to load bank statement');
    }
  };

  // View the cash-in-hand statement (uses already-loaded cash transactions)
  const handleViewCashStatement = () => {
    const rows = (cashTransactions || []).map((t) => {
      const raw = t.transaction_date || t.created_at || '';
      return {
        id: t.id,
        sortKey: t.created_at || t.transaction_date || '',
        dateISO: raw.slice(0, 10),
        dateLabel: raw ? new Date(t.transaction_date || t.created_at).toLocaleDateString() : '—',
        description: t.description,
        typeLabel: (t.transaction_type || '').replace(/_/g, ' '),
        direction: (t.transaction_type === 'cash_in' || t.transaction_type === 'bank_withdrawal') ? 'in' : 'out',
        amount: t.amount,
        balance: t.balance_after,
      };
    });
    setStatementData({
      title: 'Cash Statement',
      subtitle: 'Cash in hand — all transactions',
      transactions: rows,
      currentBalance: cashBalance,
    });
    setShowStatementDialog(true);
  };


  const renderBankAccounts = () => {
    return (
      <div className="space-y-6 mt-6">
        {/* Sub-tabs for Bank Accounts section */}
        <Tabs value={bankAccountsSubTab} onValueChange={setBankAccountsSubTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="accounts">Bank Accounts</TabsTrigger>
            <TabsTrigger value="clearing-issues">Clearing Issues</TabsTrigger>
            <TabsTrigger value="pending-credits">
              Pending Credits 
              {pendingCreditsStats.count > 0 && (
                <Badge className="ml-2 bg-orange-500 text-white">{pendingCreditsStats.count}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Bank Accounts Tab */}
          <TabsContent value="accounts">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Bank Accounts ({bankAccounts.length}/5)</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-teal-300 text-teal-700 hover:bg-teal-50"
                    onClick={() => {
                      setBankTransferForm({ from_account_id: '', to_account_id: '', amount: '', description: '' });
                      setShowBankTransferDialog(true);
                    }}
                    disabled={bankAccounts.length < 2}
                    data-testid="open-bank-transfer-btn"
                    title={bankAccounts.length < 2 ? 'Add at least 2 bank accounts to transfer funds' : 'Transfer funds between your bank accounts'}
                  >
                    <ArrowLeftRight className="h-4 w-4 mr-2" />
                    Transfer Between Banks
                  </Button>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowAddAccountDialog(true)}
                    disabled={bankAccounts.length >= 5}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Bank Account
                  </Button>
                </div>
              </div>

              {/* Bank Accounts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {bankAccounts.map((account) => (
                  <Card key={account.id} className={account.is_primary ? 'border-blue-500 border-2' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {account.bank_name}
                            {account.is_primary && (
                              <Badge className="bg-blue-100 text-blue-800">Primary</Badge>
                            )}
                          </CardTitle>
                          <p className="text-sm text-gray-600">{account.account_type}</p>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() => handleViewBankStatement(account)}
                            data-testid={`bank-statement-btn-${account.id}`}
                            title="View Statement"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Statement</span>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => {
                              setAdjustBankAccount(account);
                              setAdjustBankForm({ new_balance: String(account.current_balance || 0), reason: '' });
                              setShowAdjustBankDialog(true);
                            }}
                            data-testid={`adjust-bank-balance-btn-${account.id}`}
                            title="Adjust Balance"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Adjust</span>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingAccount(account);
                              setShowEditAccountDialog(true);
                            }}
                            title="Edit Bank Details"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeleteBankAccount(account.id, account.account_number)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Account Number:</span>
                          <span className="text-sm font-mono">**** **** **** {account.account_number.slice(-4)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">IFSC Code:</span>
                          <span className="text-sm font-mono">{account.ifsc_code}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Branch:</span>
                          <span className="text-sm">{account.branch_name}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-sm font-semibold">Current Balance:</span>
                          <span className="text-lg font-bold text-green-600">₹{account.current_balance?.toLocaleString()}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Add Account Card */}
                {bankAccounts.length < 5 && (
                  <Card className="border-dashed border-2 border-gray-300">
                    <CardContent className="p-6 text-center">
                      <Button 
                        variant="ghost"
                        className="h-full w-full flex flex-col items-center space-y-2"
                        onClick={() => setShowAddAccountDialog(true)}
                      >
                        <Plus className="h-12 w-12 text-gray-400" />
                        <span className="text-gray-600">Add New Bank Account</span>
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Clearing Issues Tab */}
          <TabsContent value="clearing-issues">
            <div className="space-y-6">
              {/* Clearing Issues Header */}
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Bank Clearing Issues</h2>
                <Button 
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={() => setShowAddClearingIssueDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Clearing Issue
                </Button>
              </div>

              {/* Clearing Issues Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{clearingIssuesStats.total_issues}</div>
                    <div className="text-sm text-gray-600">Total Issues</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-600">{clearingIssuesStats.pending_issues}</div>
                    <div className="text-sm text-gray-600">Pending</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{clearingIssuesStats.resolved_issues}</div>
                    <div className="text-sm text-gray-600">Resolved</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">₹{clearingIssuesStats.pending_amount?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Pending Amount</div>
                  </CardContent>
                </Card>
              </div>

              {/* Filter by Account */}
              <div className="flex items-center gap-4">
                <Label htmlFor="account-filter">Filter by Account:</Label>
                <Select value={selectedAccountForIssues} onValueChange={setSelectedAccountForIssues}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accounts</SelectItem>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.bank_name} - {account.account_number.slice(-4)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clearing Issues List */}
              <div className="space-y-4">
                {filteredClearingIssues.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">No clearing issues found</p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredClearingIssues.map((issue) => {
                    const account = bankAccounts.find(acc => acc.id === issue.bank_account_id);
                    const statusColors = {
                      pending: 'bg-yellow-100 text-yellow-800',
                      in_progress: 'bg-blue-100 text-blue-800', 
                      resolved: 'bg-green-100 text-green-800',
                      cancelled: 'bg-red-100 text-red-800'
                    };
                    const priorityColors = {
                      low: 'bg-gray-100 text-gray-800',
                      medium: 'bg-blue-100 text-blue-800',
                      high: 'bg-orange-100 text-orange-800',
                      critical: 'bg-red-100 text-red-800'
                    };

                    return (
                      <Card key={issue.id}>
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <CardTitle className="text-lg">{issue.title}</CardTitle>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>{account?.bank_name} - {account?.account_number.slice(-4)}</span>
                                <Badge className={statusColors[issue.status]}>
                                  {issue.status.replace('_', ' ')}
                                </Badge>
                                <Badge className={priorityColors[issue.priority]}>
                                  {issue.priority}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex space-x-1">
                              {issue.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleResolveClearingIssue(issue.id)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setEditingClearingIssue(issue);
                                  setShowEditClearingIssueDialog(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleDeleteClearingIssue(issue.id, issue.title)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {issue.description && (
                              <p className="text-sm text-gray-700">{issue.description}</p>
                            )}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium">Amount:</span> ₹{issue.amount?.toLocaleString()}
                              </div>
                              <div>
                                <span className="font-medium">Issue Type:</span> {issue.issue_type.replace('_', ' ')}
                              </div>
                              <div>
                                <span className="font-medium">Issue Date:</span> {new Date(issue.issue_date).toLocaleDateString()}
                              </div>
                              {issue.expected_resolution_date && (
                                <div>
                                  <span className="font-medium">Expected Resolution:</span> {new Date(issue.expected_resolution_date).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            {(issue.reference_number || issue.bank_reference) && (
                              <div className="pt-2 border-t text-sm">
                                {issue.reference_number && (
                                  <div><span className="font-medium">Reference:</span> {issue.reference_number}</div>
                                )}
                                {issue.bank_reference && (
                                  <div><span className="font-medium">Bank Reference:</span> {issue.bank_reference}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>
          </TabsContent>

          {/* Pending Credits Tab */}
          <TabsContent value="pending-credits">
            <div className="space-y-6">
              {/* Pending Credits Header */}
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Pending Credit Purchases</h2>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Total Pending</div>
                    <div className="text-xl font-bold text-orange-600">₹{pendingCreditsStats.total_pending_amount?.toLocaleString()}</div>
                  </div>
                </div>
              </div>

              {/* Pending Credits Stats */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{pendingCreditsStats.count}</div>
                    <div className="text-sm text-gray-600">Total Expenses</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{pendingCreditsStats.vendor_count}</div>
                    <div className="text-sm text-gray-600">Vendors</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">₹{pendingCreditsStats.total_pending_amount?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Total Amount</div>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-red-700">{pendingCreditsStats.overdue_count}</div>
                    <div className="text-sm text-red-600">Overdue</div>
                    <div className="text-xs text-red-500">₹{pendingCreditsStats.overdue_amount?.toLocaleString()}</div>
                  </CardContent>
                </Card>
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-700">{pendingCreditsStats.due_soon_count}</div>
                    <div className="text-sm text-yellow-600">Due Soon (7 days)</div>
                    <div className="text-xs text-yellow-500">₹{pendingCreditsStats.due_soon_amount?.toLocaleString()}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">₹{(cashBalance + bankAccounts.reduce((sum, acc) => sum + (acc.current_balance || 0), 0))?.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Available Funds</div>
                  </CardContent>
                </Card>
              </div>

              {/* View Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Label>View Mode:</Label>
                  <Tabs value={creditsView} onValueChange={setCreditsView}>
                    <TabsList>
                      <TabsTrigger value="vendor">By Vendor</TabsTrigger>
                      <TabsTrigger value="individual">Individual</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <div className="text-sm text-gray-600">
                  {creditsView === 'vendor' ? `${vendorSummary.length} vendors` : `${pendingCredits.length} expenses`}
                </div>
              </div>

              {/* Credits List - Vendor or Individual View */}
              <div className="space-y-4">
                {creditsView === 'vendor' ? (
                  // Vendor-based View
                  vendorSummary.length === 0 ? (
                    <Card>
                      <CardContent className="p-8 text-center">
                        <svg className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <p className="text-gray-600">No pending vendor payments</p>
                        <p className="text-sm text-gray-500">All vendors are paid up!</p>
                      </CardContent>
                    </Card>
                  ) : (
                    vendorSummary.map((vendor) => {
                      const hasOverdue = vendor.overdue_count > 0;
                      const borderColor = hasOverdue ? 'border-red-200' : '';
                      const bgColor = hasOverdue ? 'bg-red-50' : '';

                      return (
                        <Card key={vendor.vendor_name} className={`${borderColor} ${bgColor}`}>
                          <CardHeader className="pb-3">
                            <div className="flex justify-between items-start">
                              <div className="space-y-2">
                                <CardTitle className="text-lg flex items-center gap-2">
                                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                  </svg>
                                  {vendor.vendor_name}
                                  {hasOverdue && (
                                    <Badge className="bg-red-100 text-red-800">
                                      {vendor.overdue_count} Overdue
                                    </Badge>
                                  )}
                                </CardTitle>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                  <div>
                                    <span className="font-medium">Total Expenses:</span> {vendor.expense_count}
                                  </div>
                                  <div>
                                    <span className="font-medium">Contact:</span> {vendor.vendor_contact || 'N/A'}
                                  </div>
                                  {hasOverdue && (
                                    <div className="text-red-600">
                                      <span className="font-medium">Overdue Amount:</span> ₹{vendor.overdue_amount?.toLocaleString()}
                                    </div>
                                  )}
                                  {vendor.vendor_address && (
                                    <div>
                                      <span className="font-medium">Address:</span> {vendor.vendor_address}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Show expense breakdown */}
                                <details className="mt-2">
                                  <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-800">
                                    View {vendor.expense_count} expense{vendor.expense_count > 1 ? 's' : ''} →
                                  </summary>
                                  <div className="mt-2 pl-4 border-l-2 border-blue-200 space-y-1">
                                    {vendor.expenses.map((expense, index) => (
                                      <div key={expense.id} className="text-xs text-gray-600 flex justify-between">
                                        <span>{expense.description}</span>
                                        <span className="font-mono">₹{expense.amount?.toLocaleString()}</span>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <div className="text-3xl font-bold text-orange-600">₹{vendor.total_amount?.toLocaleString()}</div>
                                <div className="flex space-x-1">
                                  <Button 
                                    size="sm" 
                                    onClick={() => {
                                      setPayingVendor(vendor);
                                      setShowPayVendorDialog(true);
                                    }}
                                    className={hasOverdue ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
                                  >
                                    <CreditCard className="h-4 w-4 mr-1" />
                                    {hasOverdue ? 'Pay Overdue' : 'Pay All'}
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => setCreditsView('individual')}
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      );
                    })
                  )
                ) : (
                  // Individual Expenses View (existing code)
                  pendingCredits.length === 0 ? (
                    <Card>
                      <CardContent className="p-8 text-center">
                        <svg className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <p className="text-gray-600">No pending credit purchases</p>
                        <p className="text-sm text-gray-500">All expenses are up to date!</p>
                      </CardContent>
                    </Card>
                  ) : (
                  pendingCredits.map((credit) => {
                    const today = new Date();
                    const dueDate = credit.credit_due_date ? new Date(credit.credit_due_date) : null;
                    const daysUntilDue = dueDate ? Math.floor((dueDate - today) / (1000 * 60 * 60 * 24)) : null;
                    
                    const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
                    const isDueSoon = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 7;
                    
                    let borderColor = '';
                    let bgColor = '';
                    let dueBadgeColor = '';
                    let dueBadgeText = '';
                    
                    if (isOverdue) {
                      borderColor = 'border-red-200';
                      bgColor = 'bg-red-50';
                      dueBadgeColor = 'bg-red-100 text-red-800';
                      dueBadgeText = `Overdue ${Math.abs(daysUntilDue)} days`;
                    } else if (isDueSoon) {
                      borderColor = 'border-yellow-200';
                      bgColor = 'bg-yellow-50';
                      dueBadgeColor = 'bg-yellow-100 text-yellow-800';
                      dueBadgeText = `Due in ${daysUntilDue} days`;
                    } else if (dueDate) {
                      dueBadgeColor = 'bg-blue-100 text-blue-800';
                      dueBadgeText = `Due in ${daysUntilDue} days`;
                    }

                    return (
                      <Card key={credit.id} className={`${borderColor} ${bgColor}`}>
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-start">
                            <div className="space-y-2">
                              <CardTitle className="text-lg flex items-center gap-2">
                                {credit.description}
                                {dueBadgeText && (
                                  <Badge className={dueBadgeColor}>{dueBadgeText}</Badge>
                                )}
                              </CardTitle>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span>Category: {credit.category}</span>
                                <span>Created: {new Date(credit.transaction_date).toLocaleDateString()}</span>
                                {dueDate && (
                                  <span className={isOverdue ? 'text-red-600 font-medium' : isDueSoon ? 'text-yellow-600 font-medium' : ''}>
                                    Due: {dueDate.toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              {credit.supplier_name && (
                                <div className="text-sm">
                                  <span className="font-medium text-gray-700">Vendor: </span>
                                  <span>{credit.supplier_name}</span>
                                  {credit.vendor_contact && (
                                    <span className="ml-2 text-gray-500">• {credit.vendor_contact}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <div className="text-2xl font-bold text-orange-600">₹{credit.amount?.toLocaleString()}</div>
                              <div className="flex space-x-1">
                                <Button 
                                  size="sm" 
                                  onClick={() => {
                                    setPayingCredit(credit);
                                    setShowPayCreditDialog(true);
                                  }}
                                  className={isOverdue ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
                                >
                                  <CreditCard className="h-4 w-4 mr-1" />
                                  {isOverdue ? 'Pay Overdue' : 'Pay Now'}
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDeletePendingCredit(credit.id, credit.description)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        {credit.notes && (
                          <CardContent className="pt-0">
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Notes: </span>
                              {credit.notes}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                  )
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  // Render Cash Management Tab
  const renderCashManagement = () => (
    <div className="space-y-6 mt-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Cash In Hand Management</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={handleViewCashStatement}
            data-testid="cash-statement-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            View Full Statement
          </Button>
          <Button
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={() => {
              setAdjustCashForm({ new_balance: String(cashBalance), reason: '' });
              setShowAdjustCashDialog(true);
            }}
            data-testid="adjust-cash-balance-btn"
          >
            <Edit className="h-4 w-4 mr-2" />
            Adjust Balance
          </Button>
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setShowCashTransactionDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Cash Transaction
          </Button>
        </div>
      </div>

      {/* Cash Balance Card */}
      <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <DollarSign className="h-12 w-12 opacity-80" />
              <div className="ml-4">
                <p className="text-sm opacity-90">Cash In Hand</p>
                <p className="text-3xl font-bold">₹{cashBalance.toLocaleString()}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-75">Available Cash</p>
              <p className="text-lg font-semibold">Ready for Use</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cash Transaction Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Button 
          className="h-20 bg-green-50 hover:bg-green-100 text-green-700 border-2 border-green-200"
          variant="outline"
          onClick={() => setShowCashTransactionDialog(true)}
        >
          <div className="text-center">
            <Plus className="h-6 w-6 mx-auto mb-1" />
            <div>Cash In</div>
          </div>
        </Button>
        <Button 
          className="h-20 bg-red-50 hover:bg-red-100 text-red-700 border-2 border-red-200"
          variant="outline"
          onClick={() => setShowCashTransactionDialog(true)}
        >
          <div className="text-center">
            <Minus className="h-6 w-6 mx-auto mb-1" />
            <div>Cash Out</div>
          </div>
        </Button>
        <Button 
          className="h-20 bg-blue-50 hover:bg-blue-100 text-blue-700 border-2 border-blue-200"
          variant="outline"
          onClick={() => setShowCashTransactionDialog(true)}
        >
          <div className="text-center">
            <BarChart3 className="h-6 w-6 mx-auto mb-1" />
            <div>Bank Deposit</div>
          </div>
        </Button>
        <Button 
          className="h-20 bg-purple-50 hover:bg-purple-100 text-purple-700 border-2 border-purple-200"
          variant="outline"
          onClick={() => setShowCashTransactionDialog(true)}
        >
          <div className="text-center">
            <Download className="h-6 w-6 mx-auto mb-1" />
            <div>Bank Withdrawal</div>
          </div>
        </Button>
      </div>

      {/* Recent Cash Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Cash Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {cashTransactions.slice(0, 10).map((transaction, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    transaction.transaction_type === 'cash_in' || transaction.transaction_type === 'bank_withdrawal'
                      ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {transaction.transaction_type === 'cash_in' || transaction.transaction_type === 'bank_withdrawal' ? 
                      <Plus className="h-4 w-4 text-green-600" /> : 
                      <Minus className="h-4 w-4 text-red-600" />
                    }
                  </div>
                  <div>
                    <p className="font-medium">{transaction.description}</p>
                    <p className="text-sm text-gray-600 capitalize">{transaction.transaction_type.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${
                    transaction.transaction_type === 'cash_in' || transaction.transaction_type === 'bank_withdrawal'
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.transaction_type === 'cash_in' || transaction.transaction_type === 'bank_withdrawal' ? '+' : '-'}
                    ₹{transaction.amount?.toLocaleString()}
                  </div>
                  <p className="text-sm text-gray-500">Balance: ₹{transaction.balance_after?.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Credit Management Tab
  const renderCreditManagement = () => {
    return (
      <div className="space-y-6 mt-6">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Credit Management</h2>
          <div className="flex space-x-2">
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowAddCreditAccountDialog(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Credit Account
            </Button>
          </div>
        </div>

        {/* Credit Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 opacity-80" />
                <div className="ml-4">
                  <p className="text-sm opacity-90">Accounts Receivable</p>
                  <p className="text-2xl font-bold">₹{creditStats.totalReceivables.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <TrendingDown className="h-8 w-8 opacity-80" />
                <div className="ml-4">
                  <p className="text-sm opacity-90">Accounts Payable</p>
                  <p className="text-2xl font-bold">₹{creditStats.totalPayables.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <Clock className="h-8 w-8 opacity-80" />
                <div className="ml-4">
                  <p className="text-sm opacity-90">Overdue Receivables</p>
                  <p className="text-2xl font-bold">₹{creditStats.overdueReceivables.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 opacity-80" />
                <div className="ml-4">
                  <p className="text-sm opacity-90">Overdue Payables</p>
                  <p className="text-2xl font-bold">₹{creditStats.overduePayables.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Credit Accounts List */}
        <Card>
          <CardHeader>
            <CardTitle>Credit Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            {creditAccounts.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No credit accounts found. Add your first credit account to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {creditAccounts.map((account) => (
                  <Card key={account.id} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h4 className="font-semibold">{account.name}</h4>
                          <p className="text-sm text-gray-600">{account.type} • {account.contact || 'No contact'}</p>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedCreditAccount(account);
                              setCreditTransactionType('receivable');
                              setShowCreditTransactionDialog(true);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Receivable
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedCreditAccount(account);
                              setCreditTransactionType('payable');
                              setShowCreditTransactionDialog(true);
                            }}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Payable
                          </Button>
                          {account.type === 'vendor' && (
                            <Button
                              size="sm"
                              onClick={() => handleViewSupplierStatement(account)}
                              disabled={loadingStatement}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              View Statement
                            </Button>
                          )}
                          {account.type === 'vendor' && (
                            <Button
                              size="sm"
                              onClick={() => setSupplierPaymentAccount(account)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`vendor-make-payment-${account.id}`}
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Make Payment
                            </Button>
                          )}
                          {account.type === 'customer' && (
                            <Button
                              size="sm"
                              onClick={() => handleViewCustomerStatement(account)}
                              disabled={loadingStatement}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              View Statement
                            </Button>
                          )}
                          {account.type === 'customer' && (
                            <Button
                              size="sm"
                              onClick={() => setCustomerPaymentAccount(account)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`customer-receive-payment-${account.id}`}
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Receive Payment
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditingCreditAccount(account); setShowEditCreditAccountDialog(true); }}
                            data-testid={`edit-credit-account-${account.id}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteCreditAccount(account)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            data-testid={`delete-credit-account-${account.id}`}
                          >
                            <Trash className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Credit Limit</p>
                          <p className="font-medium">₹{account.credit_limit?.toLocaleString() || '0'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Outstanding</p>
                          <p className="font-medium text-orange-600">₹{account.outstanding_amount?.toLocaleString() || '0'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Available Credit</p>
                          <p className="font-medium text-green-600">₹{((account.credit_limit || 0) - (account.outstanding_amount || 0)).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Payment Terms</p>
                          <p className="font-medium">{account.payment_terms || 'Net 30'} days</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Credit Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Credit Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {creditTransactions.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-600">No credit transactions found.</p>
                </div>
              ) : (
                creditTransactions.slice(0, 10).map((transaction, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        transaction.type === 'receivable' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {transaction.type === 'receivable' ? 
                          <ArrowUpCircle className="h-4 w-4 text-green-600" /> : 
                          <ArrowDownCircle className="h-4 w-4 text-red-600" />
                        }
                      </div>
                      <div>
                        <p className="font-medium">{transaction.account_name}</p>
                        <p className="text-sm text-gray-600">{transaction.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-semibold ${
                        transaction.type === 'receivable' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'receivable' ? '+' : '-'}₹{transaction.amount?.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        Due: {new Date(transaction.due_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Financial Reports Tab
  const renderFinancialReports = () => {
    
    const generateReport = async (reportType) => {
      setLoadingReports(true);
      try {
        const requestBody = {
          period: reportPeriod,
          compare_previous: comparePrevious
        };
        
        if (reportPeriod === 'custom') {
          if (!customStartDate || !customEndDate) {
            toast.error('Please select both start and end dates for custom period');
            return;
          }
          requestBody.start_date = customStartDate;
          requestBody.end_date = customEndDate;
        }
        
        const response = await axios.post(`${backendUrl}/api/reports/${reportType}`, requestBody);
        
        if (reportType === 'profit-loss') {
          setProfitLossData(response.data);
        } else if (reportType === 'cash-flow') {
          setCashFlowData(response.data);
        }
        
        toast.success(`${reportType === 'profit-loss' ? 'Profit & Loss' : 'Cash Flow'} report generated successfully`);
      } catch (error) {
        console.error('Error generating report:', error);
        toast.error(`Failed to generate ${reportType === 'profit-loss' ? 'Profit & Loss' : 'Cash Flow'} report`);
      } finally {
        setLoadingReports(false);
      }
    };
    
    const formatCurrency = (amount) => {
      return `₹${Math.abs(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };
    
    const formatPercentage = (value) => {
      if (value === null || value === undefined) return '-';
      const sign = value >= 0 ? '+' : '';
      return `${sign}${value.toFixed(1)}%`;
    };
    
    return (
      <div className="space-y-6 mt-6">
        {/* Report Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Financial Report Generator</CardTitle>
            <CardDescription>Generate comprehensive financial reports for business analysis</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Period Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Report Period</label>
                <Select value={reportPeriod} onValueChange={setReportPeriod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="custom">Custom Period</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Date Range */}
              {reportPeriod === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Start Date</label>
                    <Input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">End Date</label>
                    <Input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
            
            {/* Options */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="comparePrevious"
                checked={comparePrevious}
                onChange={(e) => setComparePrevious(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="comparePrevious" className="text-sm font-medium">
                Compare with previous period
              </label>
            </div>
            
            {/* Generate Buttons */}
            <div className="flex space-x-3">
              <Button 
                onClick={() => generateReport('profit-loss')}
                disabled={loadingReports}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loadingReports ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Generate P&L Report
              </Button>
              <Button 
                onClick={() => generateReport('cash-flow')}
                disabled={loadingReports}
                variant="outline"
              >
                {loadingReports ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
                Generate Cash Flow Report
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Reports Display */}
        <Tabs value={reportsTab} onValueChange={setReportsTab}>
          <TabsList>
            <TabsTrigger value="profit-loss">Profit & Loss Statement</TabsTrigger>
            <TabsTrigger value="cash-flow">Cash Flow Statement</TabsTrigger>
          </TabsList>
          
          {/* Profit & Loss Statement */}
          <TabsContent value="profit-loss">
            {profitLossData ? (
              <Card>
                <CardHeader>
                  <CardTitle>Profit & Loss Statement</CardTitle>
                  <CardDescription>
                    Period: {new Date(profitLossData.period_start).toLocaleDateString()} to {new Date(profitLossData.period_end).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    
                    {/* Revenue Section */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-green-700">REVENUE</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                          <span>Service Revenue</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.service_revenue.current_amount)}</span>
                            {profitLossData.service_revenue.percentage_change !== null && (
                              <Badge variant={profitLossData.service_revenue.percentage_change >= 0 ? 'default' : 'destructive'}>
                                {formatPercentage(profitLossData.service_revenue.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                          <span>Parts Revenue</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.parts_revenue.current_amount)}</span>
                            {profitLossData.parts_revenue.percentage_change !== null && (
                              <Badge variant={profitLossData.parts_revenue.percentage_change >= 0 ? 'default' : 'destructive'}>
                                {formatPercentage(profitLossData.parts_revenue.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                          <span>Other Revenue</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.other_revenue.current_amount)}</span>
                            {profitLossData.other_revenue.percentage_change !== null && (
                              <Badge variant={profitLossData.other_revenue.percentage_change >= 0 ? 'default' : 'destructive'}>
                                {formatPercentage(profitLossData.other_revenue.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-green-100 rounded font-semibold">
                          <span>Total Revenue</span>
                          <div className="flex items-center space-x-4">
                            <span>{formatCurrency(profitLossData.total_revenue.current_amount)}</span>
                            {profitLossData.total_revenue.percentage_change !== null && (
                              <Badge variant={profitLossData.total_revenue.percentage_change >= 0 ? 'default' : 'destructive'}>
                                {formatPercentage(profitLossData.total_revenue.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expenses Section */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-red-700">EXPENSES</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                          <span>Parts Expense</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.parts_expense.current_amount)}</span>
                            {profitLossData.parts_expense.percentage_change !== null && (
                              <Badge variant={profitLossData.parts_expense.percentage_change >= 0 ? 'destructive' : 'default'}>
                                {formatPercentage(profitLossData.parts_expense.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                          <span>Employee Expense</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.employee_expense.current_amount)}</span>
                            {profitLossData.employee_expense.percentage_change !== null && (
                              <Badge variant={profitLossData.employee_expense.percentage_change >= 0 ? 'destructive' : 'default'}>
                                {formatPercentage(profitLossData.employee_expense.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                          <span>Operational Expense</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.operational_expense.current_amount)}</span>
                            {profitLossData.operational_expense.percentage_change !== null && (
                              <Badge variant={profitLossData.operational_expense.percentage_change >= 0 ? 'destructive' : 'default'}>
                                {formatPercentage(profitLossData.operational_expense.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                          <span>Utilities & Rent</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.utilities_expense.current_amount + profitLossData.rent_expense.current_amount)}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                          <span>Other Expenses</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.maintenance_expense.current_amount + profitLossData.equipment_expense.current_amount + profitLossData.supplies_expense.current_amount + profitLossData.other_expense.current_amount)}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-red-100 rounded font-semibold">
                          <span>Total Expenses</span>
                          <div className="flex items-center space-x-4">
                            <span>{formatCurrency(profitLossData.total_expenses.current_amount)}</span>
                            {profitLossData.total_expenses.percentage_change !== null && (
                              <Badge variant={profitLossData.total_expenses.percentage_change >= 0 ? 'destructive' : 'default'}>
                                {formatPercentage(profitLossData.total_expenses.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Summary Section */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">SUMMARY</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                          <span>Gross Profit</span>
                          <div className="flex items-center space-x-4">
                            <span className="font-medium">{formatCurrency(profitLossData.gross_profit.current_amount)}</span>
                            {profitLossData.gross_profit.percentage_change !== null && (
                              <Badge variant={profitLossData.gross_profit.percentage_change >= 0 ? 'default' : 'destructive'}>
                                {formatPercentage(profitLossData.gross_profit.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className={`flex justify-between items-center p-4 rounded font-bold text-lg ${
                          profitLossData.net_profit.current_amount >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          <span>Net Profit</span>
                          <div className="flex items-center space-x-4">
                            <span>{profitLossData.net_profit.current_amount >= 0 ? '+' : '-'}{formatCurrency(profitLossData.net_profit.current_amount)}</span>
                            {profitLossData.net_profit.percentage_change !== null && (
                              <Badge variant={profitLossData.net_profit.percentage_change >= 0 ? 'default' : 'destructive'}>
                                {formatPercentage(profitLossData.net_profit.percentage_change)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-100 rounded">
                          <span>Profit Margin</span>
                          <span className="font-medium">{profitLossData.profit_margin_percentage.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">Generate a Profit & Loss report to view detailed financial analysis</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Cash Flow Statement */}
          <TabsContent value="cash-flow">
            {cashFlowData ? (
              <Card>
                <CardHeader>
                  <CardTitle>Cash Flow Statement</CardTitle>
                  <CardDescription>
                    Period: {new Date(cashFlowData.period_start).toLocaleDateString()} to {new Date(cashFlowData.period_end).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    
                    {/* Cash Balance Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-gray-600">Opening Cash</p>
                          <p className="text-2xl font-bold">{formatCurrency(cashFlowData.opening_cash)}</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-gray-600">Net Change</p>
                          <p className={`text-2xl font-bold ${cashFlowData.net_cash_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {cashFlowData.net_cash_change >= 0 ? '+' : ''}{formatCurrency(cashFlowData.net_cash_change)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4 text-center">
                          <p className="text-sm text-gray-600">Closing Cash</p>
                          <p className="text-2xl font-bold">{formatCurrency(cashFlowData.closing_cash)}</p>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Operating Activities */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-blue-700">OPERATING ACTIVITIES</h3>
                      <div className="space-y-2">
                        {cashFlowData.operating_activities.map((item, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-blue-50 rounded">
                            <span>{item.category}</span>
                            <div className="flex space-x-4 text-sm">
                              {item.inflow > 0 && <span className="text-green-600">+{formatCurrency(item.inflow)}</span>}
                              {item.outflow > 0 && <span className="text-red-600">-{formatCurrency(item.outflow)}</span>}
                              <span className={`font-medium ${item.net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.net_flow >= 0 ? '+' : ''}{formatCurrency(item.net_flow)}
                              </span>
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-between items-center p-3 bg-blue-100 rounded font-semibold">
                          <span>Total Operating Cash Flow</span>
                          <span className={cashFlowData.total_operating_flow >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {cashFlowData.total_operating_flow >= 0 ? '+' : ''}{formatCurrency(cashFlowData.total_operating_flow)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Investing Activities */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-purple-700">INVESTING ACTIVITIES</h3>
                      <div className="space-y-2">
                        {cashFlowData.investing_activities.length > 0 ? (
                          cashFlowData.investing_activities.map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-purple-50 rounded">
                              <span>{item.category}</span>
                              <div className="flex space-x-4 text-sm">
                                {item.inflow > 0 && <span className="text-green-600">+{formatCurrency(item.inflow)}</span>}
                                {item.outflow > 0 && <span className="text-red-600">-{formatCurrency(item.outflow)}</span>}
                                <span className={`font-medium ${item.net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {item.net_flow >= 0 ? '+' : ''}{formatCurrency(item.net_flow)}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 bg-purple-50 rounded text-center text-gray-500">
                            No investing activities in this period
                          </div>
                        )}
                        <div className="flex justify-between items-center p-3 bg-purple-100 rounded font-semibold">
                          <span>Total Investing Cash Flow</span>
                          <span className={cashFlowData.total_investing_flow >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {cashFlowData.total_investing_flow >= 0 ? '+' : ''}{formatCurrency(cashFlowData.total_investing_flow)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Financing Activities */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-orange-700">FINANCING ACTIVITIES</h3>
                      <div className="space-y-2">
                        {cashFlowData.financing_activities.length > 0 ? (
                          cashFlowData.financing_activities.map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-orange-50 rounded">
                              <span>{item.category}</span>
                              <div className="flex space-x-4 text-sm">
                                {item.inflow > 0 && <span className="text-green-600">+{formatCurrency(item.inflow)}</span>}
                                {item.outflow > 0 && <span className="text-red-600">-{formatCurrency(item.outflow)}</span>}
                                <span className={`font-medium ${item.net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {item.net_flow >= 0 ? '+' : ''}{formatCurrency(item.net_flow)}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 bg-orange-50 rounded text-center text-gray-500">
                            No financing activities in this period
                          </div>
                        )}
                        <div className="flex justify-between items-center p-3 bg-orange-100 rounded font-semibold">
                          <span>Total Financing Cash Flow</span>
                          <span className={cashFlowData.total_financing_flow >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {cashFlowData.total_financing_flow >= 0 ? '+' : ''}{formatCurrency(cashFlowData.total_financing_flow)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <TrendingUp className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">Generate a Cash Flow report to view cash movement analysis</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading accounting data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accounting & Banking</h1>
          <p className="text-gray-600">Financial overview, bank accounts, and cash management</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Button variant="outline" className="mt-1" onClick={() => setShowPLReport(true)} data-testid="open-pl-report-btn">
            Profit &amp; Loss Statement
          </Button>
          <Button variant="outline" className="mt-1" onClick={() => setShowGSTReport(true)} data-testid="open-gst-summary-btn">
            GST Summary
          </Button>
          {activeTab !== 'advance-payments' && (
          <div>
            <Label className="text-xs text-gray-600">Period</Label>
            <Select value={dateRange} onValueChange={setDateRange} data-testid="accounting-date-range-select">
              <SelectTrigger className="w-40 mt-1" data-testid="accounting-date-range-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}
          {activeTab !== 'advance-payments' && dateRange === 'custom' && (
            <>
              <div>
                <Label className="text-xs text-gray-600">From</Label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-40 mt-1"
                  data-testid="accounting-custom-start-date"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">To</Label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-40 mt-1"
                  data-testid="accounting-custom-end-date"
                />
              </div>
            </>
          )}
          {activeTab !== 'advance-payments' && dateRange !== 'all' && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-1" data-testid="accounting-compare-toggle-label">
              <input
                type="checkbox"
                checked={comparePreviousPeriod}
                onChange={(e) => setComparePreviousPeriod(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="accounting-compare-toggle"
              />
              <span>Compare to previous period</span>
            </label>
          )}
        </div>
      </div>

      <ProfitLossReport open={showPLReport} onOpenChange={setShowPLReport} />
      <GSTSummaryReport open={showGSTReport} onOpenChange={setShowGSTReport} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="dashboard">Financial Dashboard</TabsTrigger>
          <TabsTrigger value="bank-accounts">Bank Accounts</TabsTrigger>
          <TabsTrigger value="cash">Cash Management</TabsTrigger>
          <TabsTrigger value="credit-management">Credit Management</TabsTrigger>
          <TabsTrigger value="advance-payments" data-testid="accounting-tab-advance-payments">Advance Payments</TabsTrigger>
          <TabsTrigger value="recurring-budget" data-testid="accounting-tab-recurring-budget">Recurring & Budget</TabsTrigger>
          <TabsTrigger value="reports">Financial Reports</TabsTrigger>
          <TabsTrigger value="full-statement" data-testid="accounting-tab-full-statement">Full Statement</TabsTrigger>
          <TabsTrigger value="opening-balances" data-testid="accounting-tab-opening-balances">Opening Balances</TabsTrigger>
        </TabsList>

        {/* Financial Dashboard Tab */}
        <TabsContent value="dashboard">{renderFinancialDashboard()}</TabsContent>

        {/* Bank Accounts Tab */}
        <TabsContent value="bank-accounts">{renderBankAccounts()}</TabsContent>

        {/* Cash Management Tab */}
        <TabsContent value="cash">{renderCashManagement()}</TabsContent>

        {/* Credit Management Tab */}
        <TabsContent value="credit-management">{renderCreditManagement()}</TabsContent>

        {/* Advance Payments Tab */}
        <TabsContent value="advance-payments"><AdvancePaymentsPanel /></TabsContent>

        {/* Recurring Expenses & Budget Tab */}
        <TabsContent value="recurring-budget">
          <div className="mt-6"><RecurringExpensesSection /></div>
        </TabsContent>

        {/* Financial Reports Tab */}
        <TabsContent value="reports">{renderFinancialReports()}</TabsContent>

        {/* Full Statement Tab */}
        <TabsContent value="full-statement"><CompleteFinancialStatement /></TabsContent>

        {/* Opening Balances Tab */}
        <TabsContent value="opening-balances">
          <div className="mt-6"><OpeningBalancesSection /></div>
        </TabsContent>
      </Tabs>

      {/* Add Expense Dialog */}
      <Dialog open={showAddExpenseDialog} onOpenChange={setShowAddExpenseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>
              Record a new expense transaction
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitExpense(onSubmitExpense)} className="space-y-4">
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Enter expense description"
                {...registerExpense("description", { 
                  required: "Description is required",
                  minLength: { value: 3, message: "Description must be at least 3 characters" }
                })}
              />
              {expenseErrors.description && (
                <p className="text-red-500 text-sm mt-1">{expenseErrors.description.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="amount">Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="Enter amount"
                {...registerExpense("amount", { 
                  required: "Amount is required",
                  min: { value: 0.01, message: "Amount must be greater than 0" },
                  pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" }
                })}
              />
              {expenseErrors.amount && (
                <p className="text-red-500 text-sm mt-1">{expenseErrors.amount.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Controller
                name="category"
                control={controlExpense}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {expenseErrors.category && (
                <p className="text-red-500 text-sm mt-1">{expenseErrors.category.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                {...registerExpense("date", { required: "Date is required" })}
              />
              {expenseErrors.date && (
                <p className="text-red-500 text-sm mt-1">{expenseErrors.date.message}</p>
              )}
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  setShowAddExpenseDialog(false);
                  resetExpense();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isExpenseSubmitting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isExpenseSubmitting ? 'Adding...' : 'Add Expense'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Income Dialog */}
      <Dialog open={showAddIncomeDialog} onOpenChange={setShowAddIncomeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Income</DialogTitle>
            <DialogDescription>
              Record a new income/revenue transaction
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitIncome(onSubmitIncome)} className="space-y-4">
            <div>
              <Label htmlFor="income-description">Description</Label>
              <Input
                id="income-description"
                placeholder="Enter income description (e.g., Service Revenue)"
                {...registerIncome("description", { 
                  required: "Description is required",
                  minLength: { value: 3, message: "Description must be at least 3 characters" }
                })}
              />
              {incomeErrors.description && (
                <p className="text-red-500 text-sm mt-1">{incomeErrors.description.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="income-amount">Amount (₹)</Label>
              <Input
                id="income-amount"
                type="number"
                step="0.01"
                placeholder="Enter amount"
                {...registerIncome("amount", { 
                  required: "Amount is required",
                  min: { value: 0.01, message: "Amount must be greater than 0" },
                  pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Enter valid amount" }
                })}
              />
              {incomeErrors.amount && (
                <p className="text-red-500 text-sm mt-1">{incomeErrors.amount.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="income-category">Category</Label>
              <Controller
                name="category"
                control={controlIncome}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {incomeCategories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {incomeErrors.category && (
                <p className="text-red-500 text-sm mt-1">{incomeErrors.category.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="income-date">Date</Label>
              <Input
                id="income-date"
                type="date"
                {...registerIncome("date", { required: "Date is required" })}
              />
              {incomeErrors.date && (
                <p className="text-red-500 text-sm mt-1">{incomeErrors.date.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="income-payment-method">Credit To *</Label>
              <Controller
                name="payment_method"
                control={controlIncome}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger data-testid="income-payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_account">Bank Account</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              {incomePaymentMethod === 'cash' ? (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Current Cash Balance:</span>
                  <span className="font-semibold text-green-600">₹{cashBalance.toLocaleString()}</span>
                </div>
              ) : (
                <div>
                  <Label htmlFor="income-bank-account">Select Bank Account *</Label>
                  <Controller
                    name="bank_account_id"
                    control={controlIncome}
                    rules={{ required: incomePaymentMethod === 'bank_account' ? "Bank account selection is required" : false }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="mt-2" data-testid="income-bank-account-select">
                          <SelectValue placeholder="Choose bank account" />
                        </SelectTrigger>
                        <SelectContent>
                          {bankAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              <div className="flex flex-col">
                                <span>{account.bank_name} - **** {(account.account_number || '').slice(-4)}</span>
                                <span className="text-xs text-gray-500">
                                  Balance: ₹{account.current_balance?.toLocaleString()}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {incomeErrors.bank_account_id && (
                    <p className="text-red-500 text-sm mt-1">{incomeErrors.bank_account_id.message}</p>
                  )}
                  {bankAccounts.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No bank accounts yet — add one under Bank Accounts first.</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  setShowAddIncomeDialog(false);
                  resetIncome();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={isIncomeSubmitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {isIncomeSubmitting ? 'Adding...' : 'Add Income'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Bank Account Dialog */}
      <Dialog open={showAddAccountDialog} onOpenChange={setShowAddAccountDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
            <DialogDescription>
              Add a new bank account to your system (Maximum 5 accounts allowed)
            </DialogDescription>
          </DialogHeader>
          <AddBankAccountForm 
            onSubmit={handleAddBankAccount}
            onCancel={() => setShowAddAccountDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Bank Account Dialog */}
      <Dialog open={showEditAccountDialog} onOpenChange={setShowEditAccountDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Bank Account</DialogTitle>
            <DialogDescription>
              Update bank account information
            </DialogDescription>
          </DialogHeader>
          {editingAccount && (
            <EditBankAccountForm 
              account={editingAccount}
              onSubmit={(data) => handleEditBankAccount(editingAccount.id, data)}
              onCancel={() => {
                setShowEditAccountDialog(false);
                setEditingAccount(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Cash Transaction Dialog */}
      <Dialog open={showCashTransactionDialog} onOpenChange={setShowCashTransactionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Cash Transaction</DialogTitle>
            <DialogDescription>
              Record a cash in hand transaction
            </DialogDescription>
          </DialogHeader>
          <AddCashTransactionForm 
            bankAccounts={bankAccounts}
            currentBalance={cashBalance}
            onSubmit={handleCashTransaction}
            onCancel={() => setShowCashTransactionDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Adjust Cash Balance Dialog */}
      <Dialog open={showAdjustCashDialog} onOpenChange={setShowAdjustCashDialog}>
        <DialogContent className="max-w-md" data-testid="adjust-cash-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Cash Balance</DialogTitle>
            <DialogDescription>
              Set the cash-in-hand to a specific amount (e.g., after a physical count).
              A balancing cash-in or cash-out transaction will be auto-created with your reason for the audit trail.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-500">Current Balance</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="adjust-cash-current">
                ₹{cashBalance.toLocaleString()}
              </p>
            </div>

            <div>
              <Label htmlFor="adjust-new-balance">
                New Balance <span className="text-red-500">*</span>
              </Label>
              <Input
                id="adjust-new-balance"
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter target cash amount"
                value={adjustCashForm.new_balance}
                onChange={(e) => setAdjustCashForm(prev => ({ ...prev, new_balance: e.target.value }))}
                data-testid="adjust-cash-new-balance-input"
                className="mt-1"
              />
              {adjustCashForm.new_balance !== '' && !isNaN(parseFloat(adjustCashForm.new_balance)) && (
                (() => {
                  const target = parseFloat(adjustCashForm.new_balance);
                  const delta = target - cashBalance;
                  if (delta === 0) {
                    return <p className="text-xs text-gray-500 mt-1">No change from current balance</p>;
                  }
                  const isUp = delta > 0;
                  return (
                    <p className={`text-xs mt-1 ${isUp ? 'text-green-700' : 'text-red-700'}`} data-testid="adjust-cash-delta-preview">
                      This will create a <strong>{isUp ? 'Cash In' : 'Cash Out'}</strong> of ₹{Math.abs(delta).toLocaleString()}
                    </p>
                  );
                })()
              )}
            </div>

            <div>
              <Label htmlFor="adjust-reason">
                Reason for Adjustment <span className="text-red-500">*</span>
              </Label>
              <textarea
                id="adjust-reason"
                rows={2}
                placeholder="e.g. Physical cash count discrepancy, opening balance correction"
                value={adjustCashForm.reason}
                onChange={(e) => setAdjustCashForm(prev => ({ ...prev, reason: e.target.value }))}
                data-testid="adjust-cash-reason-input"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowAdjustCashDialog(false)}
              disabled={adjustCashSubmitting}
              data-testid="adjust-cash-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              disabled={adjustCashSubmitting}
              data-testid="adjust-cash-submit-btn"
              onClick={async () => {
                const target = parseFloat(adjustCashForm.new_balance);
                if (isNaN(target) || target < 0) {
                  toast.error("Please enter a valid balance amount");
                  return;
                }
                if (!adjustCashForm.reason.trim()) {
                  toast.error("Please provide a reason for the adjustment");
                  return;
                }
                if (target === cashBalance) {
                  toast.error("New balance equals current balance — nothing to adjust");
                  return;
                }
                setAdjustCashSubmitting(true);
                try {
                  await axios.post(`${backendUrl}/api/cash/adjust-balance`, {
                    new_balance: target,
                    reason: adjustCashForm.reason.trim(),
                  });
                  toast.success(`Cash balance updated to ₹${target.toLocaleString()}`);
                  setShowAdjustCashDialog(false);
                  setAdjustCashForm({ new_balance: '', reason: '' });
                  loadCashData();
                  if (onUpdate) onUpdate();
                } catch (e) {
                  const msg = e.response?.data?.detail || 'Failed to adjust balance';
                  toast.error(msg);
                } finally {
                  setAdjustCashSubmitting(false);
                }
              }}
            >
              {adjustCashSubmitting ? 'Adjusting…' : 'Adjust Balance'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Bank Balance Dialog */}
      <Dialog
        open={showAdjustBankDialog}
        onOpenChange={(open) => {
          setShowAdjustBankDialog(open);
          if (!open) {
            setAdjustBankAccount(null);
            setAdjustBankForm({ new_balance: '', reason: '' });
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="adjust-bank-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Bank Balance</DialogTitle>
            <DialogDescription>
              Set this bank account's balance to match your physical bank statement.
              A reconciliation audit record will be created. This adjustment does not affect revenue or expenses.
            </DialogDescription>
          </DialogHeader>

          {adjustBankAccount && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs text-gray-500">{adjustBankAccount.bank_name} — ****{(adjustBankAccount.account_number || '').slice(-4)}</p>
                <p className="text-xs text-gray-500 mt-2">Current Balance</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="adjust-bank-current">
                  ₹{(adjustBankAccount.current_balance || 0).toLocaleString()}
                </p>
              </div>

              <div>
                <Label htmlFor="adjust-bank-new-balance">
                  New Balance <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="adjust-bank-new-balance"
                  type="number"
                  step="0.01"
                  placeholder="Enter target bank balance"
                  value={adjustBankForm.new_balance}
                  onChange={(e) => setAdjustBankForm(prev => ({ ...prev, new_balance: e.target.value }))}
                  data-testid="adjust-bank-new-balance-input"
                  className="mt-1"
                />
                {adjustBankForm.new_balance !== '' && !isNaN(parseFloat(adjustBankForm.new_balance)) && (
                  (() => {
                    const target = parseFloat(adjustBankForm.new_balance);
                    const delta = target - (adjustBankAccount.current_balance || 0);
                    if (delta === 0) {
                      return <p className="text-xs text-gray-500 mt-1">No change from current balance</p>;
                    }
                    const isUp = delta > 0;
                    return (
                      <p className={`text-xs mt-1 ${isUp ? 'text-green-700' : 'text-red-700'}`} data-testid="adjust-bank-delta-preview">
                        This will be recorded as a <strong>{isUp ? 'Credit' : 'Debit'}</strong> reconciliation of ₹{Math.abs(delta).toLocaleString()}
                      </p>
                    );
                  })()
                )}
              </div>

              <div>
                <Label htmlFor="adjust-bank-reason">
                  Reason for Adjustment <span className="text-red-500">*</span>
                </Label>
                <textarea
                  id="adjust-bank-reason"
                  rows={2}
                  placeholder="e.g. Bank statement reconciliation, unrecorded charges, opening balance correction"
                  value={adjustBankForm.reason}
                  onChange={(e) => setAdjustBankForm(prev => ({ ...prev, reason: e.target.value }))}
                  data-testid="adjust-bank-reason-input"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowAdjustBankDialog(false)}
              disabled={adjustBankSubmitting}
              data-testid="adjust-bank-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              disabled={adjustBankSubmitting}
              data-testid="adjust-bank-submit-btn"
              onClick={async () => {
                if (!adjustBankAccount) return;
                const target = parseFloat(adjustBankForm.new_balance);
                if (isNaN(target)) {
                  toast.error("Please enter a valid balance amount");
                  return;
                }
                if (!adjustBankForm.reason.trim()) {
                  toast.error("Please provide a reason for the adjustment");
                  return;
                }
                if (target === (adjustBankAccount.current_balance || 0)) {
                  toast.error("New balance equals current balance — nothing to adjust");
                  return;
                }
                setAdjustBankSubmitting(true);
                try {
                  await axios.post(
                    `${backendUrl}/api/bank-accounts/${adjustBankAccount.id}/adjust-balance`,
                    {
                      new_balance: target,
                      reason: adjustBankForm.reason.trim(),
                    }
                  );
                  toast.success(`Bank balance updated to ₹${target.toLocaleString()}`);
                  setShowAdjustBankDialog(false);
                  setAdjustBankAccount(null);
                  setAdjustBankForm({ new_balance: '', reason: '' });
                  if (typeof loadBankAccounts === 'function') {
                    loadBankAccounts();
                  }
                  if (onUpdate) onUpdate();
                } catch (e) {
                  const msg = e.response?.data?.detail || 'Failed to adjust bank balance';
                  toast.error(msg);
                } finally {
                  setAdjustBankSubmitting(false);
                }
              }}
            >
              {adjustBankSubmitting ? 'Adjusting…' : 'Adjust Balance'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inter-bank Transfer Dialog */}
      <Dialog open={showBankTransferDialog} onOpenChange={setShowBankTransferDialog}>
        <DialogContent data-testid="bank-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-teal-600" />
              Transfer Between Bank Accounts
            </DialogTitle>
            <DialogDescription>
              Move funds directly between two of your bank accounts. This does not affect revenue or expenses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>From Account *</Label>
              <Select value={bankTransferForm.from_account_id} onValueChange={(v) => setBankTransferForm(f => ({ ...f, from_account_id: v }))}>
                <SelectTrigger data-testid="bank-transfer-from-select">
                  <SelectValue placeholder="Select source account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.filter(a => a.id !== bankTransferForm.to_account_id).map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.bank_name} - {a.account_number} (₹{Number(a.current_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Account *</Label>
              <Select value={bankTransferForm.to_account_id} onValueChange={(v) => setBankTransferForm(f => ({ ...f, to_account_id: v }))}>
                <SelectTrigger data-testid="bank-transfer-to-select">
                  <SelectValue placeholder="Select destination account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.filter(a => a.id !== bankTransferForm.from_account_id).map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.bank_name} - {a.account_number} (₹{Number(a.current_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={bankTransferForm.amount}
                onChange={(e) => setBankTransferForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                data-testid="bank-transfer-amount-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={bankTransferForm.description}
                onChange={(e) => setBankTransferForm(f => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Sweeping surplus funds into savings"
                rows={2}
                data-testid="bank-transfer-description-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowBankTransferDialog(false)} disabled={bankTransferSubmitting}>Cancel</Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              disabled={bankTransferSubmitting || !bankTransferForm.from_account_id || !bankTransferForm.to_account_id || !bankTransferForm.amount}
              data-testid="bank-transfer-submit-btn"
              onClick={async () => {
                if (bankTransferForm.from_account_id === bankTransferForm.to_account_id) {
                  toast.error('Source and destination accounts must be different');
                  return;
                }
                const amt = parseFloat(bankTransferForm.amount);
                if (!amt || amt <= 0) {
                  toast.error('Enter a valid transfer amount');
                  return;
                }
                setBankTransferSubmitting(true);
                try {
                  const res = await axios.post(`${backendUrl}/api/bank-accounts/transfer`, {
                    from_account_id: bankTransferForm.from_account_id,
                    to_account_id: bankTransferForm.to_account_id,
                    amount: amt,
                    description: bankTransferForm.description || null,
                  });
                  toast.success(res.data?.message || 'Transfer completed successfully');
                  setShowBankTransferDialog(false);
                  await loadBankAccounts();
                  if (onUpdate) onUpdate();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to transfer funds');
                } finally {
                  setBankTransferSubmitting(false);
                }
              }}
            >
              {bankTransferSubmitting ? 'Transferring…' : 'Transfer Funds'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Supplier Make Payment Dialog */}
      {supplierPaymentAccount && (
        <SupplierPaymentDialog
          open={!!supplierPaymentAccount}
          onOpenChange={(o) => { if (!o) setSupplierPaymentAccount(null); }}
          supplierName={supplierPaymentAccount.name}
          basePayload={{
            credit_account_id: supplierPaymentAccount.id,
            supplier_id: supplierPaymentAccount.supplier_id || null,
            supplier_name: supplierPaymentAccount.name,
            target: "all",
          }}
          onPaid={loadCreditAccounts}
        />
      )}

      {/* Customer Receive Payment Dialog */}
      {customerPaymentAccount && (
        <SupplierPaymentDialog
          open={!!customerPaymentAccount}
          onOpenChange={(o) => { if (!o) setCustomerPaymentAccount(null); }}
          kind="customer"
          supplierName={customerPaymentAccount.name}
          basePayload={{
            credit_account_id: customerPaymentAccount.id,
            target: "all",
          }}
          onPaid={loadCreditAccounts}
        />
      )}

      {/* Bank / Cash Statement Dialog */}
      {statementData && (
        <AccountStatementDialog
          open={showStatementDialog}
          onOpenChange={(o) => { setShowStatementDialog(o); if (!o) setStatementData(null); }}
          title={statementData.title}
          subtitle={statementData.subtitle}
          transactions={statementData.transactions}
          currentBalance={statementData.currentBalance}
        />
      )}


      {/* Add Credit Account Dialog */}
      <Dialog open={showAddCreditAccountDialog} onOpenChange={setShowAddCreditAccountDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Credit Account</DialogTitle>
            <DialogDescription>
              Add a new customer or vendor credit account
            </DialogDescription>
          </DialogHeader>
          <AddCreditAccountForm
            onSubmit={handleAddCreditAccount}
            onCancel={() => setShowAddCreditAccountDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Credit Account Dialog */}
      <Dialog open={showEditCreditAccountDialog} onOpenChange={(o) => { if (!o) { setShowEditCreditAccountDialog(false); setEditingCreditAccount(null); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Credit Account</DialogTitle>
            <DialogDescription>
              Update account details. Switch "Account Type" to Vendor to get a Supplier Statement.
            </DialogDescription>
          </DialogHeader>
          {editingCreditAccount && (
            <AddCreditAccountForm
              existingAccount={editingCreditAccount}
              onSubmit={handleUpdateCreditAccount}
              onCancel={() => { setShowEditCreditAccountDialog(false); setEditingCreditAccount(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Credit Transaction Dialog */}
      <Dialog open={showCreditTransactionDialog} onOpenChange={setShowCreditTransactionDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add {creditTransactionType === 'receivable' ? 'Receivable' : 'Payable'}
            </DialogTitle>
            <DialogDescription>
              Record a new credit {creditTransactionType} for {selectedCreditAccount?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedCreditAccount && (
            <AddCreditTransactionForm
              account={selectedCreditAccount}
              transactionType={creditTransactionType}
              onSubmit={handleAddCreditTransaction}
              onCancel={() => {
                setShowCreditTransactionDialog(false);
                setSelectedCreditAccount(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Clearing Issue Dialog */}
      <Dialog open={showAddClearingIssueDialog} onOpenChange={setShowAddClearingIssueDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Bank Clearing Issue</DialogTitle>
            <DialogDescription>
              Record a new bank clearing issue for tracking
            </DialogDescription>
          </DialogHeader>
          <AddClearingIssueForm 
            bankAccounts={bankAccounts}
            onSubmit={handleAddClearingIssue}
            onCancel={() => setShowAddClearingIssueDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Clearing Issue Dialog */}
      <Dialog open={showEditClearingIssueDialog} onOpenChange={setShowEditClearingIssueDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Bank Clearing Issue</DialogTitle>
            <DialogDescription>
              Update clearing issue details and status
            </DialogDescription>
          </DialogHeader>
          {editingClearingIssue && (
            <EditClearingIssueForm 
              bankAccounts={bankAccounts}
              clearingIssue={editingClearingIssue}
              onSubmit={handleEditClearingIssue}
              onCancel={() => {
                setShowEditClearingIssueDialog(false);
                setEditingClearingIssue(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Pay Credit Expense Dialog */}
      <Dialog open={showPayCreditDialog} onOpenChange={setShowPayCreditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pay Credit Expense</DialogTitle>
            <DialogDescription>
              Process payment for the pending credit purchase
            </DialogDescription>
          </DialogHeader>
          {payingCredit && (
            <PayCreditForm 
              credit={payingCredit}
              bankAccounts={bankAccounts}
              cashBalance={cashBalance}
              onSubmit={(paymentData) => handlePayCredit(payingCredit, paymentData)}
              onCancel={() => {
                setShowPayCreditDialog(false);
                setPayingCredit(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Pay Vendor Dialog */}
      <Dialog open={showPayVendorDialog} onOpenChange={setShowPayVendorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Pay Vendor - All Credits</DialogTitle>
            <DialogDescription>
              Pay all pending credit expenses for this vendor
            </DialogDescription>
          </DialogHeader>
          {payingVendor && (
            <PayVendorForm 
              vendor={payingVendor}
              bankAccounts={bankAccounts}
              cashBalance={cashBalance}
              onSubmit={(paymentData) => handlePayVendor(payingVendor, paymentData)}
              onCancel={() => {
                setShowPayVendorDialog(false);
                setPayingVendor(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Supplier Statement Dialog */}
      <Dialog open={showSupplierStatementDialog} onOpenChange={setShowSupplierStatementDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Supplier Statement - {selectedCreditAccount?.name}</DialogTitle>
            <DialogDescription>
              Comprehensive accounting statement for supplier transactions
            </DialogDescription>
          </DialogHeader>
          {supplierStatementData && (
            <SupplierStatementView
              statementData={supplierStatementData}
              startDate={statementStart}
              endDate={statementEnd}
              loading={loadingStatement}
              onApplyFilter={(s, e, bt) => handleViewSupplierStatement(selectedCreditAccount, s, e, bt)}
              onClose={() => {
                setShowSupplierStatementDialog(false);
                setSupplierStatementData(null);
                setSelectedCreditAccount(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Statement Dialog */}
      <Dialog open={showCustomerStatementDialog} onOpenChange={setShowCustomerStatementDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customer Statement - {selectedCreditAccount?.name}</DialogTitle>
            <DialogDescription>
              Comprehensive accounting statement for customer transactions
            </DialogDescription>
          </DialogHeader>
          {customerStatementData && (
            <CustomerStatementView
              statementData={customerStatementData}
              startDate={statementStart}
              endDate={statementEnd}
              loading={loadingStatement}
              onApplyFilter={(s, e) => handleViewCustomerStatement(selectedCreditAccount, s, e)}
              onClose={() => {
                setShowCustomerStatementDialog(false);
                setCustomerStatementData(null);
                setSelectedCreditAccount(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Add Bank Account Form Component - React Hook Form Implementation
const AddBankAccountForm = ({ onSubmit, onCancel }) => {
  const { register, handleSubmit, control, formState: { errors }, reset, setValue, watch } = useForm({
    defaultValues: {
      bank_name: '',
      account_number: '',
      account_holder_name: '',
      account_type: 'current',
      ifsc_code: '',
      branch_name: '',
      branch_address: '',
      current_balance: '',
      is_primary: false,
      notes: ''
    }
  });

  const accountTypes = [
    { value: 'savings', label: 'Savings' },
    { value: 'current', label: 'Current' },
    { value: 'fixed_deposit', label: 'Fixed Deposit' },
    { value: 'overdraft', label: 'Overdraft' }
  ];

  const onSubmitForm = (data) => {
    const submitData = {
      ...data,
      current_balance: parseFloat(data.current_balance) || 0
    };
    
    onSubmit(submitData);
    reset(); // Clear form after successful submission
  };

  const handleCancel = () => {
    reset(); // Clear form when canceling
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
      <div>
        <Label htmlFor="bank_name">Bank Name *</Label>
        <Input
          id="bank_name"
          placeholder="e.g., State Bank of India"
          {...register("bank_name", { 
            required: "Bank name is required",
            minLength: { value: 2, message: "Bank name must be at least 2 characters" }
          })}
        />
        {errors.bank_name && <p className="text-red-500 text-sm mt-1">{errors.bank_name.message}</p>}
      </div>
      
      <div>
        <Label htmlFor="account_holder_name">Account Holder Name *</Label>
        <Input
          id="account_holder_name"
          placeholder="Account holder's full name"
          {...register("account_holder_name", { 
            required: "Account holder name is required",
            minLength: { value: 2, message: "Name must be at least 2 characters" }
          })}
        />
        {errors.account_holder_name && <p className="text-red-500 text-sm mt-1">{errors.account_holder_name.message}</p>}
      </div>

      <div>
        <Label htmlFor="account_number">Account Number *</Label>
        <Input
          id="account_number"
          placeholder="12 digit account number"
          {...register("account_number", { 
            required: "Account number is required",
            minLength: { value: 8, message: "Account number must be at least 8 digits" },
            pattern: { value: /^[0-9]+$/, message: "Account number must contain only numbers" }
          })}
        />
        {errors.account_number && <p className="text-red-500 text-sm mt-1">{errors.account_number.message}</p>}
      </div>

      <div>
        <Label htmlFor="account_type">Account Type</Label>
        <Controller
          name="account_type"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {accountTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div>
        <Label htmlFor="ifsc_code">IFSC Code *</Label>
        <Input
          id="ifsc_code"
          placeholder="e.g., SBIN0001234"
          {...register("ifsc_code", { 
            required: "IFSC code is required",
            pattern: { 
              value: /^[A-Z]{4}0[A-Z0-9]{6}$/, 
              message: "Invalid IFSC code format (e.g., SBIN0001234)" 
            },
            onChange: (e) => setValue("ifsc_code", e.target.value.toUpperCase())
          })}
        />
        {errors.ifsc_code && <p className="text-red-500 text-sm mt-1">{errors.ifsc_code.message}</p>}
      </div>

      <div>
        <Label htmlFor="branch_name">Branch Name *</Label>
        <Input
          id="branch_name"
          placeholder="Branch name"
          {...register("branch_name", { 
            required: "Branch name is required",
            minLength: { value: 2, message: "Branch name must be at least 2 characters" }
          })}
        />
        {errors.branch_name && <p className="text-red-500 text-sm mt-1">{errors.branch_name.message}</p>}
      </div>

      <div>
        <Label htmlFor="branch_address">Branch Address (Optional)</Label>
        <Input
          id="branch_address"
          placeholder="Branch address"
          {...register("branch_address")}
        />
      </div>

      <div>
        <Label htmlFor="current_balance">Current Balance (₹)</Label>
        <Input
          id="current_balance"
          type="number"
          placeholder="0"
          min="0"
          step="0.01"
          {...register("current_balance", {
            min: { value: 0, message: "Balance cannot be negative" }
          })}
        />
        {errors.current_balance && <p className="text-red-500 text-sm mt-1">{errors.current_balance.message}</p>}
      </div>

      <div className="flex items-center space-x-2">
        <Controller
          name="is_primary"
          control={control}
          render={({ field }) => (
            <Checkbox 
              id="is_primary"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <Label htmlFor="is_primary">Set as primary account</Label>
      </div>

      <div>
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Textarea
          id="notes"
          placeholder="Additional notes..."
          rows={2}
          {...register("notes")}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
          Add Bank Account
        </Button>
      </div>
    </form>
  );
};

// Edit Bank Account Form Component - React Hook Form Implementation
const EditBankAccountForm = ({ account, onSubmit, onCancel }) => {
  const { register, handleSubmit, control, formState: { errors }, reset, setValue, watch } = useForm({
    defaultValues: {
      bank_name: account?.bank_name || '',
      account_holder_name: account?.account_holder_name || '',
      account_type: account?.account_type || 'current',
      ifsc_code: account?.ifsc_code || '',
      branch_name: account?.branch_name || '',
      branch_address: account?.branch_address || '',
      current_balance: account?.current_balance?.toString() || '',
      is_primary: account?.is_primary || false,
      notes: account?.notes || ''
    }
  });

  const accountTypes = [
    { value: 'savings', label: 'Savings' },
    { value: 'current', label: 'Current' },
    { value: 'fixed_deposit', label: 'Fixed Deposit' },
    { value: 'overdraft', label: 'Overdraft' }
  ];

  const onSubmitForm = (data) => {
    const updateData = {
      ...data,
      current_balance: parseFloat(data.current_balance) || 0
    };
    
    onSubmit(updateData);
  };

  const handleCancel = () => {
    reset(); // Reset form to original values
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
      <div>
        <Label htmlFor="edit_bank_name">Bank Name *</Label>
        <Input
          id="edit_bank_name"
          {...register("bank_name", { 
            required: "Bank name is required",
            minLength: { value: 2, message: "Bank name must be at least 2 characters" }
          })}
        />
        {errors.bank_name && <p className="text-red-500 text-sm mt-1">{errors.bank_name.message}</p>}
      </div>
      
      <div>
        <Label htmlFor="edit_account_holder_name">Account Holder Name *</Label>
        <Input
          id="edit_account_holder_name"
          {...register("account_holder_name", { 
            required: "Account holder name is required",
            minLength: { value: 2, message: "Name must be at least 2 characters" }
          })}
        />
        {errors.account_holder_name && <p className="text-red-500 text-sm mt-1">{errors.account_holder_name.message}</p>}
      </div>

      <div>
        <Label htmlFor="edit_account_type">Account Type</Label>
        <Controller
          name="account_type"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {accountTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div>
        <Label htmlFor="edit_ifsc_code">IFSC Code *</Label>
        <Input
          id="edit_ifsc_code"
          {...register("ifsc_code", { 
            required: "IFSC code is required",
            pattern: { 
              value: /^[A-Z]{4}0[A-Z0-9]{6}$/, 
              message: "Invalid IFSC code format (e.g., SBIN0001234)" 
            },
            onChange: (e) => setValue("ifsc_code", e.target.value.toUpperCase())
          })}
        />
        {errors.ifsc_code && <p className="text-red-500 text-sm mt-1">{errors.ifsc_code.message}</p>}
      </div>

      <div>
        <Label htmlFor="edit_branch_name">Branch Name *</Label>
        <Input
          id="edit_branch_name"
          {...register("branch_name", { 
            required: "Branch name is required",
            minLength: { value: 2, message: "Branch name must be at least 2 characters" }
          })}
        />
        {errors.branch_name && <p className="text-red-500 text-sm mt-1">{errors.branch_name.message}</p>}
      </div>

      <div>
        <Label htmlFor="edit_branch_address">Branch Address</Label>
        <Input
          id="edit_branch_address"
          {...register("branch_address")}
        />
      </div>

      <div>
        <Label htmlFor="edit_current_balance">Current Balance (₹)</Label>
        <Input
          id="edit_current_balance"
          type="number"
          min="0"
          step="0.01"
          {...register("current_balance", {
            min: { value: 0, message: "Balance cannot be negative" }
          })}
        />
        {errors.current_balance && <p className="text-red-500 text-sm mt-1">{errors.current_balance.message}</p>}
      </div>

      <div className="flex items-center space-x-2">
        <Controller
          name="is_primary"
          control={control}
          render={({ field }) => (
            <Checkbox 
              id="edit_is_primary"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <Label htmlFor="edit_is_primary">Set as primary account</Label>
      </div>

      <div>
        <Label htmlFor="edit_notes">Notes (Optional)</Label>
        <Textarea
          id="edit_notes"
          placeholder="Additional notes..."
          rows={2}
          {...register("notes")}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
          Update Account
        </Button>
      </div>
    </form>
  );
};

// Add Cash Transaction Form Component - React Hook Form Implementation
const AddCashTransactionForm = ({ bankAccounts, currentBalance, onSubmit, onCancel }) => {
  const { register, handleSubmit, control, formState: { errors }, reset, setValue, watch } = useForm({
    defaultValues: {
      transaction_type: 'cash_in',
      amount: '',
      description: '',
      bank_account_id: '',
      transaction_date: new Date().toISOString().split('T')[0],
      notes: ''
    }
  });

  const transactionTypes = [
    { value: 'cash_in', label: 'Cash In', description: 'Add cash to hand', color: 'green' },
    { value: 'cash_out', label: 'Cash Out', description: 'Spend cash from hand', color: 'red' },
    { value: 'bank_deposit', label: 'Bank Deposit', description: 'Deposit cash to bank', color: 'blue' },
    { value: 'bank_withdrawal', label: 'Bank Withdrawal', description: 'Withdraw cash from bank', color: 'purple' }
  ];

  const watchedTransactionType = watch("transaction_type");
  const selectedType = transactionTypes.find(type => type.value === watchedTransactionType);
  const requiresBankAccount = ['bank_deposit', 'bank_withdrawal'].includes(watchedTransactionType);

  const onSubmitForm = (data) => {
    const amount = parseFloat(data.amount);
    
    if (amount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    if (requiresBankAccount && !data.bank_account_id) {
      toast.error('Please select a bank account for this transaction');
      return;
    }

    // Check for sufficient cash balance for outgoing transactions
    if (['cash_out', 'bank_deposit'].includes(data.transaction_type) && amount > currentBalance) {
      toast.error(`Insufficient cash balance. Available: ₹${currentBalance.toLocaleString()}`);
      return;
    }

    const submitData = {
      ...data,
      amount: amount
    };

    onSubmit(submitData);
    reset(); // Clear form after successful submission
  };

  const handleCancel = () => {
    reset(); // Clear form when canceling
    onCancel();
  };

  const handleTransactionTypeChange = (value) => {
    setValue("transaction_type", value);
    if (!['bank_deposit', 'bank_withdrawal'].includes(value)) {
      setValue("bank_account_id", ''); // Clear bank account when not needed
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
      <div>
        <Label htmlFor="transaction_type">Transaction Type</Label>
        <Controller
          name="transaction_type"
          control={control}
          render={({ field }) => (
            <Select 
              onValueChange={(value) => {
                field.onChange(value);
                handleTransactionTypeChange(value);
              }} 
              defaultValue={field.value}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {transactionTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex flex-col">
                      <span>{type.label}</span>
                      <span className="text-xs text-gray-500">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Current Cash Balance:</span>
          <span className="font-semibold text-green-600">₹{currentBalance.toLocaleString()}</span>
        </div>
        {selectedType && (
          <p className={`text-xs mt-1 text-${selectedType.color}-600`}>
            {selectedType.description}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="amount">Amount (₹) *</Label>
        <Input
          id="amount"
          type="number"
          placeholder="Enter amount"
          min="0.01"
          step="0.01"
          {...register("amount", {
            required: "Amount is required",
            min: { value: 0.01, message: "Amount must be greater than 0" },
            validate: {
              sufficientBalance: (value) => {
                const amount = parseFloat(value);
                if (['cash_out', 'bank_deposit'].includes(watchedTransactionType) && amount > currentBalance) {
                  return `Insufficient cash balance. Available: ₹${currentBalance.toLocaleString()}`;
                }
                return true;
              }
            }
          })}
        />
        {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>}
      </div>

      <div>
        <Label htmlFor="description">Description *</Label>
        <Input
          id="description"
          placeholder="e.g., Office supplies purchase, Customer payment"
          {...register("description", {
            required: "Description is required",
            minLength: { value: 3, message: "Description must be at least 3 characters" }
          })}
        />
        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
      </div>

      {requiresBankAccount && (
        <div>
          <Label htmlFor="bank_account_id">Bank Account *</Label>
          <Controller
            name="bank_account_id"
            control={control}
            rules={{
              required: requiresBankAccount ? "Bank account is required for this transaction" : false
            }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      <div className="flex flex-col">
                        <span>{account.bank_name} - **** {(account.account_number || '').slice(-4)}</span>
                        <span className="text-xs text-gray-500">Balance: ₹{account.current_balance?.toLocaleString()}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.bank_account_id && <p className="text-red-500 text-sm mt-1">{errors.bank_account_id.message}</p>}
        </div>
      )}

      <div>
        <Label htmlFor="transaction_date">Transaction Date</Label>
        <Input
          id="transaction_date"
          type="date"
          {...register("transaction_date", {
            required: "Transaction date is required"
          })}
        />
        {errors.transaction_date && <p className="text-red-500 text-sm mt-1">{errors.transaction_date.message}</p>}
      </div>

      <div>
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Textarea
          id="notes"
          placeholder="Additional notes..."
          rows={2}
          {...register("notes")}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-green-600 hover:bg-green-700">
          Record Transaction
        </Button>
      </div>
    </form>
  );
};

// ============ Bank Clearing Issues Form Components ============

// Add Clearing Issue Form Component
const AddClearingIssueForm = ({ bankAccounts, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    bank_account_id: '',
    issue_type: 'pending_deposit',
    title: '',
    description: '',
    amount: '',
    priority: 'medium',
    issue_date: new Date().toISOString().split('T')[0],
    expected_resolution_date: '',
    reference_number: '',
    bank_reference: '',
    notes: ''
  });

  const issueTypes = [
    { value: 'cheque_clearance', label: 'Cheque Clearance' },
    { value: 'pending_deposit', label: 'Pending Deposit' },
    { value: 'pending_withdrawal', label: 'Pending Withdrawal' },
    { value: 'bank_charges', label: 'Bank Charges' },
    { value: 'interest_pending', label: 'Interest Pending' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'gray' },
    { value: 'medium', label: 'Medium', color: 'blue' },
    { value: 'high', label: 'High', color: 'orange' },
    { value: 'critical', label: 'Critical', color: 'red' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.bank_account_id || !formData.title || !formData.amount) {
      toast.error('Please fill in bank account, title and amount');
      return;
    }

    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount),
      expected_resolution_date: formData.expected_resolution_date || null
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Bank Account Selection */}
      <div>
        <Label htmlFor="bank_account">Bank Account *</Label>
        <Select value={formData.bank_account_id} onValueChange={(value) => setFormData(prev => ({...prev, bank_account_id: value}))}>
          <SelectTrigger>
            <SelectValue placeholder="Select bank account" />
          </SelectTrigger>
          <SelectContent>
            {bankAccounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {account.bank_name} - {account.account_number.slice(-4)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Issue Type */}
      <div>
        <Label htmlFor="issue_type">Issue Type</Label>
        <Select value={formData.issue_type} onValueChange={(value) => setFormData(prev => ({...prev, issue_type: value}))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {issueTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Title */}
      <div>
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          placeholder="Enter issue title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
        />
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Enter issue description (optional)"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
          rows={3}
        />
      </div>

      {/* Amount and Priority */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="amount">Amount (₹) *</Label>
          <Input
            id="amount"
            type="number"
            placeholder="Enter amount"
            value={formData.amount}
            onChange={(e) => setFormData(prev => ({...prev, amount: e.target.value}))}
            min="0"
            step="0.01"
          />
        </div>
        <div>
          <Label htmlFor="priority">Priority</Label>
          <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({...prev, priority: value}))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priorities.map((priority) => (
                <SelectItem key={priority.value} value={priority.value}>
                  {priority.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="issue_date">Issue Date</Label>
          <Input
            id="issue_date"
            type="date"
            value={formData.issue_date}
            onChange={(e) => setFormData(prev => ({...prev, issue_date: e.target.value}))}
          />
        </div>
        <div>
          <Label htmlFor="expected_resolution_date">Expected Resolution Date</Label>
          <Input
            id="expected_resolution_date"
            type="date"
            value={formData.expected_resolution_date}
            onChange={(e) => setFormData(prev => ({...prev, expected_resolution_date: e.target.value}))}
          />
        </div>
      </div>

      {/* Reference Numbers */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="reference_number">Reference Number</Label>
          <Input
            id="reference_number"
            placeholder="Cheque number, transaction ID, etc."
            value={formData.reference_number}
            onChange={(e) => setFormData(prev => ({...prev, reference_number: e.target.value}))}
          />
        </div>
        <div>
          <Label htmlFor="bank_reference">Bank Reference</Label>
          <Input
            id="bank_reference"
            placeholder="Bank's reference number"
            value={formData.bank_reference}
            onChange={(e) => setFormData(prev => ({...prev, bank_reference: e.target.value}))}
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Additional notes (optional)"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
          rows={2}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
          Add Clearing Issue
        </Button>
      </div>
    </form>
  );
};

// Edit Clearing Issue Form Component
const EditClearingIssueForm = ({ bankAccounts, clearingIssue, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    issue_type: clearingIssue.issue_type || 'pending_deposit',
    title: clearingIssue.title || '',
    description: clearingIssue.description || '',
    amount: clearingIssue.amount?.toString() || '',
    status: clearingIssue.status || 'pending',
    priority: clearingIssue.priority || 'medium',
    expected_resolution_date: clearingIssue.expected_resolution_date || '',
    actual_resolution_date: clearingIssue.actual_resolution_date || '',
    reference_number: clearingIssue.reference_number || '',
    bank_reference: clearingIssue.bank_reference || '',
    notes: clearingIssue.notes || ''
  });

  const issueTypes = [
    { value: 'cheque_clearance', label: 'Cheque Clearance' },
    { value: 'pending_deposit', label: 'Pending Deposit' },
    { value: 'pending_withdrawal', label: 'Pending Withdrawal' },
    { value: 'bank_charges', label: 'Bank Charges' },
    { value: 'interest_pending', label: 'Interest Pending' },
    { value: 'other', label: 'Other' }
  ];

  const statuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.amount) {
      toast.error('Please fill in title and amount');
      return;
    }

    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount),
      expected_resolution_date: formData.expected_resolution_date || null,
      actual_resolution_date: formData.actual_resolution_date || null
    };

    onSubmit(clearingIssue.id, submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Bank Account (Read-only) */}
      <div>
        <Label>Bank Account</Label>
        <div className="p-2 border rounded bg-gray-50">
          {bankAccounts.find(acc => acc.id === clearingIssue.bank_account_id)?.bank_name} - 
          {bankAccounts.find(acc => acc.id === clearingIssue.bank_account_id)?.account_number.slice(-4)}
        </div>
      </div>

      {/* Issue Type and Status */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="issue_type">Issue Type</Label>
          <Select value={formData.issue_type} onValueChange={(value) => setFormData(prev => ({...prev, issue_type: value}))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {issueTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="status">Status</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({...prev, status: value}))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Title */}
      <div>
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          placeholder="Enter issue title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
        />
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Enter issue description (optional)"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
          rows={3}
        />
      </div>

      {/* Amount and Priority */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="amount">Amount (₹) *</Label>
          <Input
            id="amount"
            type="number"
            placeholder="Enter amount"
            value={formData.amount}
            onChange={(e) => setFormData(prev => ({...prev, amount: e.target.value}))}
            min="0"
            step="0.01"
          />
        </div>
        <div>
          <Label htmlFor="priority">Priority</Label>
          <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({...prev, priority: value}))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priorities.map((priority) => (
                <SelectItem key={priority.value} value={priority.value}>
                  {priority.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="expected_resolution_date">Expected Resolution Date</Label>
          <Input
            id="expected_resolution_date"
            type="date"
            value={formData.expected_resolution_date}
            onChange={(e) => setFormData(prev => ({...prev, expected_resolution_date: e.target.value}))}
          />
        </div>
        <div>
          <Label htmlFor="actual_resolution_date">Actual Resolution Date</Label>
          <Input
            id="actual_resolution_date"
            type="date"
            value={formData.actual_resolution_date}
            onChange={(e) => setFormData(prev => ({...prev, actual_resolution_date: e.target.value}))}
          />
        </div>
      </div>

      {/* Reference Numbers */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="reference_number">Reference Number</Label>
          <Input
            id="reference_number"
            placeholder="Cheque number, transaction ID, etc."
            value={formData.reference_number}
            onChange={(e) => setFormData(prev => ({...prev, reference_number: e.target.value}))}
          />
        </div>
        <div>
          <Label htmlFor="bank_reference">Bank Reference</Label>
          <Input
            id="bank_reference"
            placeholder="Bank's reference number"
            value={formData.bank_reference}
            onChange={(e) => setFormData(prev => ({...prev, bank_reference: e.target.value}))}
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Additional notes (optional)"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
          rows={2}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
          Update Clearing Issue
        </Button>
      </div>
    </form>
  );
};

// ============ Pay Credit Expense Form Component ============

const PayCreditForm = ({ credit, bankAccounts, cashBalance, onSubmit, onCancel }) => {
  const { register, handleSubmit, control, formState: { errors }, watch } = useForm({
    defaultValues: {
      payment_method: 'cash',
      bank_account_id: ''
    }
  });

  const paymentMethod = watch("payment_method");

  const onSubmitForm = (data) => {
    if (data.payment_method === 'cash' && credit.amount > cashBalance) {
      toast.error(`Insufficient cash balance. Available: ₹${cashBalance.toLocaleString()}`);
      return;
    }

    if (data.payment_method === 'bank_account' && !data.bank_account_id) {
      toast.error('Please select a bank account');
      return;
    }

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
      {/* Credit Details */}
      <div className="p-4 bg-orange-50 rounded-lg">
        <h3 className="font-medium text-orange-900 mb-2">Payment Details</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Description:</span>
            <span className="font-medium">{credit.description}</span>
          </div>
          <div className="flex justify-between">
            <span>Vendor:</span>
            <span>{credit.supplier_name || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span>Amount Due:</span>
            <span className="font-bold text-orange-600 text-lg">₹{credit.amount?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span>Date Created:</span>
            <span>{new Date(credit.transaction_date).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div>
        <Label htmlFor="payment_method">Payment Method *</Label>
        <Controller
          name="payment_method"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank_account">Bank Account</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Payment Method Details */}
      <div className="p-3 bg-gray-50 rounded-lg">
        {paymentMethod === 'cash' ? (
          <div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Available Cash Balance:</span>
              <span className="font-semibold text-green-600">₹{cashBalance.toLocaleString()}</span>
            </div>
            {credit.amount > cashBalance && (
              <p className="text-red-500 text-sm mt-2">
                ⚠️ Insufficient cash balance! Need ₹{(credit.amount - cashBalance).toLocaleString()} more.
              </p>
            )}
          </div>
        ) : paymentMethod === 'bank_account' ? (
          <div>
            <Label htmlFor="bank_account_id">Select Bank Account *</Label>
            <Controller
              name="bank_account_id"
              control={control}
              rules={{ required: "Bank account selection is required" }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choose bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex flex-col">
                          <span>{account.bank_name} - **** {(account.account_number || '').slice(-4)}</span>
                          <span className="text-xs text-gray-500">
                            Balance: ₹{account.current_balance?.toLocaleString()}
                            {account.current_balance < credit.amount && (
                              <span className="text-red-500"> (Insufficient)</span>
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.bank_account_id && (
              <p className="text-red-500 text-sm mt-1">{errors.bank_account_id.message}</p>
            )}
          </div>
        ) : null}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="bg-green-600 hover:bg-green-700"
          disabled={
            (paymentMethod === 'cash' && credit.amount > cashBalance) ||
            (paymentMethod === 'bank_account' && !watch('bank_account_id'))
          }
        >
          Pay ₹{credit.amount?.toLocaleString()}
        </Button>
      </div>
    </form>
  );
};

// ============ Pay Vendor (All Credits) Form Component ============

const PayVendorForm = ({ vendor, bankAccounts, cashBalance, onSubmit, onCancel }) => {
  const { register, handleSubmit, control, formState: { errors }, watch } = useForm({
    defaultValues: {
      payment_method: 'cash',
      bank_account_id: '',
      amount: vendor.total_amount,
      partial_payment: false
    }
  });

  const paymentMethod = watch("payment_method");
  const isPartialPayment = watch("partial_payment");
  const paymentAmount = watch("amount");

  const onSubmitForm = (data) => {
    if (data.payment_method === 'cash' && data.amount > cashBalance) {
      toast.error(`Insufficient cash balance. Available: ₹${cashBalance.toLocaleString()}`);
      return;
    }

    if (data.payment_method === 'bank_account' && !data.bank_account_id) {
      toast.error('Please select a bank account');
      return;
    }

    if (data.amount <= 0 || data.amount > vendor.total_amount) {
      toast.error(`Payment amount must be between ₹1 and ₹${vendor.total_amount.toLocaleString()}`);
      return;
    }

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
      {/* Vendor Details */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Vendor Payment Details
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Vendor:</span>
            <span className="font-medium">{vendor.vendor_name}</span>
          </div>
          <div className="flex justify-between">
            <span>Contact:</span>
            <span>{vendor.vendor_contact || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Expenses:</span>
            <span>{vendor.expense_count} expense{vendor.expense_count > 1 ? 's' : ''}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Amount Due:</span>
            <span className="font-bold text-blue-600 text-lg">₹{vendor.total_amount?.toLocaleString()}</span>
          </div>
          {vendor.overdue_count > 0 && (
            <div className="flex justify-between text-red-600">
              <span>Overdue Amount:</span>
              <span className="font-bold">₹{vendor.overdue_amount?.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Payment Method Selection */}
      <div>
        <Label htmlFor="payment_method">Payment Method *</Label>
        <Controller
          name="payment_method"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank_account">Bank Account</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Payment Method Details */}
      <div className="p-3 bg-gray-50 rounded-lg">
        {paymentMethod === 'cash' ? (
          <div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Available Cash Balance:</span>
              <span className="font-semibold text-green-600">₹{cashBalance.toLocaleString()}</span>
            </div>
            {vendor.total_amount > cashBalance && (
              <p className="text-red-500 text-sm mt-2">
                ⚠️ Insufficient cash balance! Need ₹{(vendor.total_amount - cashBalance).toLocaleString()} more.
              </p>
            )}
          </div>
        ) : paymentMethod === 'bank_account' ? (
          <div>
            <Label htmlFor="bank_account_id">Select Bank Account *</Label>
            <Controller
              name="bank_account_id"
              control={control}
              rules={{ required: "Bank account selection is required" }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choose bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex flex-col">
                          <span>{account.bank_name} - **** {(account.account_number || '').slice(-4)}</span>
                          <span className="text-xs text-gray-500">
                            Balance: ₹{account.current_balance?.toLocaleString()}
                            {account.current_balance < vendor.total_amount && (
                              <span className="text-red-500"> (Insufficient for full payment)</span>
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.bank_account_id && (
              <p className="text-red-500 text-sm mt-1">{errors.bank_account_id.message}</p>
            )}
          </div>
        ) : null}
      </div>

      {/* Payment Amount */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Controller
            name="partial_payment"
            control={control}
            render={({ field }) => (
              <Checkbox 
                id="partial_payment"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <Label htmlFor="partial_payment">Partial Payment</Label>
        </div>

        {isPartialPayment ? (
          <div>
            <Label htmlFor="amount">Payment Amount (₹) *</Label>
            <Input
              id="amount"
              type="number"
              {...register("amount", { 
                required: "Amount is required",
                min: { value: 1, message: "Amount must be at least ₹1" },
                max: { value: vendor.total_amount, message: `Amount cannot exceed ₹${vendor.total_amount.toLocaleString()}` }
              })}
              min="1"
              max={vendor.total_amount}
              step="0.01"
            />
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
            )}
            <p className="text-sm text-gray-600 mt-1">
              Remaining balance: ₹{(vendor.total_amount - (paymentAmount || 0)).toLocaleString()}
            </p>
          </div>
        ) : (
          <div className="p-3 bg-green-50 rounded-lg text-center">
            <div className="text-lg font-bold text-green-700">
              Full Payment: ₹{vendor.total_amount?.toLocaleString()}
            </div>
            <p className="text-sm text-green-600">This will pay all {vendor.expense_count} pending expense(s)</p>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="bg-green-600 hover:bg-green-700"
          disabled={
            (paymentMethod === 'cash' && (paymentAmount || vendor.total_amount) > cashBalance) ||
            (paymentMethod === 'bank_account' && !watch('bank_account_id'))
          }
        >
          Pay ₹{(isPartialPayment ? paymentAmount : vendor.total_amount)?.toLocaleString()}
        </Button>
      </div>
    </form>
  );
};

// Reports Section Component
const ReportsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('this_month');
  const reportRef = useRef(null);

  useEffect(() => {
    let active = true;
    const loadReportData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/reports/analytics`, { params: { range: dateRange } });
        if (active) setData(response.data);
      } catch (error) {
        console.error('Error loading report data:', error);
        toast.error('Failed to load report data');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadReportData();
    return () => { active = false; };
  }, [dateRange]);

  const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
  const fmtHours = (h) => `${Number(h || 0).toFixed(1)}h`;

  const handlePrint = () => {
    if (!reportRef.current) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Reports & Analytics — ${data?.period?.label || ''}</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111} h1{font-size:20px} h2{font-size:15px;margin-top:20px;border-bottom:1px solid #ddd;padding-bottom:4px} table{width:100%;border-collapse:collapse;margin-top:8px} th,td{border:1px solid #ddd;padding:6px;text-align:left;font-size:12px} .kpi{display:inline-block;border:1px solid #ddd;border-radius:8px;padding:10px 16px;margin:6px}</style>
      </head><body>${reportRef.current.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  const k = data?.kpis || {};
  const CHART_COLORS = ['#2563eb', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#0ea5e9'];
  const revenueSplit = [
    { name: 'Parts', value: data?.revenue_split?.parts || 0 },
    { name: 'Labour', value: data?.revenue_split?.labour || 0 },
  ];
  const agingData = [
    { name: '0-30d', amount: data?.receivables_aging?.current || 0 },
    { name: '31-60d', amount: data?.receivables_aging?.d31_60 || 0 },
    { name: '61-90d', amount: data?.receivables_aging?.d61_90 || 0 },
    { name: '90d+', amount: data?.receivables_aging?.d90_plus || 0 },
  ];

  return (
    <div className="space-y-6" ref={reportRef} data-testid="reports-analytics-section">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Business insights and performance metrics · <span className="font-medium">{data?.period?.label}</span></p>
        </div>
        <div className="flex space-x-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40" data-testid="reports-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={handlePrint} data-testid="reports-export-btn">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white" data-testid="kpi-total-vehicles">
          <CardContent className="p-4">
            <Car className="h-6 w-6 opacity-80 mb-2" />
            <p className="text-xs opacity-90">Total Vehicles</p>
            <p className="text-2xl font-bold">{k.total_vehicles ?? 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white" data-testid="kpi-completed-jobs">
          <CardContent className="p-4">
            <CheckCircle className="h-6 w-6 opacity-80 mb-2" />
            <p className="text-xs opacity-90">Completed Jobs</p>
            <p className="text-2xl font-bold">{k.completed_jobs ?? 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white" data-testid="kpi-active-jobs">
          <CardContent className="p-4">
            <Wrench className="h-6 w-6 opacity-80 mb-2" />
            <p className="text-xs opacity-90">Active Jobs</p>
            <p className="text-2xl font-bold">{k.active_jobs ?? 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white" data-testid="kpi-revenue">
          <CardContent className="p-4">
            <DollarSign className="h-6 w-6 opacity-80 mb-2" />
            <p className="text-xs opacity-90">Revenue</p>
            <p className="text-2xl font-bold">{fmtINR(k.revenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white" data-testid="kpi-avg-job-value">
          <CardContent className="p-4">
            <TrendingUp className="h-6 w-6 opacity-80 mb-2" />
            <p className="text-xs opacity-90">Avg Job Value</p>
            <p className="text-2xl font-bold">{fmtINR(k.avg_job_value)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white" data-testid="kpi-outstanding-receivables">
          <CardContent className="p-4">
            <AlertCircle className="h-6 w-6 opacity-80 mb-2" />
            <p className="text-xs opacity-90">Receivables</p>
            <p className="text-2xl font-bold">{fmtINR(k.outstanding_receivables)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="revenue-trend-chart">
          <CardHeader><CardTitle className="text-base">Revenue Trend (Last 6 Months)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data?.revenue_trend || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card data-testid="jobs-trend-chart">
          <CardHeader><CardTitle className="text-base">Jobs Completed (Last 6 Months)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data?.jobs_trend || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Revenue split + Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="revenue-split-chart">
          <CardHeader><CardTitle className="text-base">Revenue Split — Parts vs Labour</CardTitle></CardHeader>
          <CardContent>
            {(revenueSplit[0].value + revenueSplit[1].value) > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={revenueSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={55} label={(e) => `${e.name}: ${fmtINR(e.value)}`}>
                    {revenueSplit.map((entry, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtINR(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">No revenue in this period</div>}
          </CardContent>
        </Card>
        <Card data-testid="receivables-aging-chart">
          <CardHeader><CardTitle className="text-base">Receivables Aging</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={agingData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                  {agingData.map((entry, i) => <Cell key={i} fill={['#10b981', '#f59e0b', '#f97316', '#ef4444'][i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Services + Top Parts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="top-services-list">
          <CardHeader><CardTitle className="text-base">Top Services</CardTitle></CardHeader>
          <CardContent>
            {(data?.top_services || []).length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No service sales in this period</p>
            ) : (
              <div className="space-y-2">
                {data.top_services.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-600">{item.count} time(s)</p>
                    </div>
                    <p className="font-semibold text-gray-900 shrink-0">{fmtINR(item.revenue)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card data-testid="top-parts-list">
          <CardHeader><CardTitle className="text-base">Top Parts Sold</CardTitle></CardHeader>
          <CardContent>
            {(data?.top_parts || []).length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No parts sold in this period</p>
            ) : (
              <div className="space-y-2">
                {data.top_parts.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-600">Qty: {item.qty}</p>
                    </div>
                    <p className="font-semibold text-gray-900 shrink-0">{fmtINR(item.revenue)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Customers + Technician Productivity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="top-customers-list">
          <CardHeader><CardTitle className="text-base">Top Customers</CardTitle></CardHeader>
          <CardContent>
            {(data?.top_customers || []).length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No customer billing in this period</p>
            ) : (
              <div className="space-y-2">
                {data.top_customers.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-600">{item.invoices} invoice(s)</p>
                    </div>
                    <p className="font-semibold text-gray-900 shrink-0">{fmtINR(item.spend)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card data-testid="technician-productivity-list">
          <CardHeader>
            <CardTitle className="text-base">Technician Productivity</CardTitle>
            <CardDescription>Avg turnaround: <span className="font-medium">{data?.avg_turnaround_days ?? 0} days</span></CardDescription>
          </CardHeader>
          <CardContent>
            {(data?.technician_productivity || []).length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">No time logged in this period</p>
            ) : (
              <div className="space-y-2">
                {data.technician_productivity.map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{t.technician}</p>
                      <p className="text-xs text-gray-600">{t.jobs} job(s) · {t.efficiency}% billable</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-gray-900">{fmtHours(t.billable_hours)}</p>
                      <p className="text-xs text-gray-500">of {fmtHours(t.total_hours)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Low Stock */}
      <Card data-testid="low-stock-list">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <PackageX className="h-5 w-5 text-red-500" /> Low / Out of Stock Parts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.low_stock || []).length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">All parts are sufficiently stocked 🎉</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">Part</th>
                    <th className="py-2 pr-4">Part No.</th>
                    <th className="py-2 pr-4 text-right">In Stock</th>
                    <th className="py-2 pr-4 text-right">Min</th>
                    <th className="py-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.low_stock.map((p, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium text-gray-900">{p.part_name}</td>
                      <td className="py-2 pr-4 text-gray-600">{p.part_number || '—'}</td>
                      <td className="py-2 pr-4 text-right">{p.stock}</td>
                      <td className="py-2 pr-4 text-right">{p.minimum}</td>
                      <td className="py-2 text-right">
                        <Badge className={p.out_of_stock ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                          {p.out_of_stock ? 'Out of Stock' : 'Low'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AddCreditAccountForm = ({ onSubmit, onCancel, existingAccount = null }) => {
  const [formData, setFormData] = useState({
    name: existingAccount?.name || '',
    type: existingAccount?.type || 'customer',
    supplier_id: existingAccount?.supplier_id || '',
    contact: existingAccount?.contact || '',
    email: existingAccount?.email || '',
    address: existingAccount?.address || '',
    credit_limit: existingAccount?.credit_limit != null ? String(existingAccount.credit_limit) : '',
    payment_terms: existingAccount?.payment_terms != null ? String(existingAccount.payment_terms) : '30',
    notes: existingAccount?.notes || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    const loadParties = async () => {
      try {
        const [supRes, custRes] = await Promise.all([
          axios.get(`${backendUrl}/api/suppliers`),
          axios.get(`${backendUrl}/api/customers`),
        ]);
        setSuppliers(Array.isArray(supRes.data) ? supRes.data : []);
        setCustomers(Array.isArray(custRes.data) ? custRes.data : []);
      } catch (e) {
        console.error('Failed to load suppliers/customers', e);
      }
    };
    loadParties();
  }, [backendUrl]);

  const buildAddress = (s) => [s.address_line1, s.address_line2, s.city, s.state, s.postal_code, s.country]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(', ');

  // Autofill account details from a selected supplier
  const handleSupplierSelect = (e) => {
    const supplierId = e.target.value;
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) {
      setFormData((prev) => ({ ...prev, supplier_id: '' }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      supplier_id: supplier.id,
      name: supplier.name || prev.name,
      contact: supplier.phone || prev.contact,
      email: supplier.email || prev.email,
      address: buildAddress(supplier) || prev.address,
      credit_limit: supplier.credit_limit != null ? String(supplier.credit_limit) : prev.credit_limit,
      payment_terms: supplier.payment_terms != null ? String(supplier.payment_terms) : prev.payment_terms,
    }));
  };

  // Autofill account details from a selected customer
  const handleCustomerSelect = (e) => {
    const id = e.target.value;
    setCustomerId(id);
    const customer = customers.find((c) => c.id === id);
    if (!customer) return;
    setFormData((prev) => ({
      ...prev,
      name: customer.name || prev.name,
      contact: customer.contact || prev.contact,
      email: customer.email || prev.email,
      address: customer.location || prev.address,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Changing fields manually breaks the supplier link only if name is edited
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const accountData = {
        ...formData,
        credit_limit: parseFloat(formData.credit_limit) || 0,
        payment_terms: parseInt(formData.payment_terms) || 30,
        outstanding_amount: 0
      };

      const isEdit = !!existingAccount;
      const url = isEdit
        ? `${backendUrl}/api/credit-accounts/${existingAccount.id}`
        : `${backendUrl}/api/credit-accounts`;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(accountData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Credit account for ${formData.name} ${isEdit ? 'updated' : 'created'} successfully!`);
        onSubmit(result);
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${isEdit ? 'update' : 'create'} credit account`);
      }
    } catch (error) {
      console.error('Error creating credit account:', error);
      toast.error('Failed to create credit account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Account Name *</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Customer or vendor name"
          required
          className="no-uppercase"
        />
      </div>

      <div>
        <Label htmlFor="type">Account Type *</Label>
        <select
          id="type"
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
          required
        >
          <option value="customer">Customer (Receivables)</option>
          <option value="vendor">Vendor (Payables)</option>
        </select>
      </div>

      {/* Fetch details from an existing customer (customer accounts) */}
      {formData.type === 'customer' && (
        <div>
          <Label htmlFor="customer_select">Fetch from Customer (optional)</Label>
          <select
            id="customer_select"
            name="customer_select"
            value={customerId}
            onChange={handleCustomerSelect}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
            data-testid="credit-account-customer-select"
          >
            <option value="">-- Select a customer to auto-fill --</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Selecting a customer fills in name, contact, email and address.</p>
        </div>
      )}

      {/* Fetch details from an existing supplier (vendor accounts) */}
      {formData.type === 'vendor' && (
        <div>
          <Label htmlFor="supplier_select">Fetch from Supplier (optional)</Label>
          <select
            id="supplier_select"
            name="supplier_select"
            value={formData.supplier_id}
            onChange={handleSupplierSelect}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
            data-testid="credit-account-supplier-select"
          >
            <option value="">-- Select a supplier to auto-fill --</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Selecting a supplier fills in name, contact, address, credit limit and payment terms.</p>
        </div>
      )}

      <div>
        <Label htmlFor="contact">Contact Number</Label>
        <Input
          id="contact"
          name="contact"
          value={formData.contact}
          onChange={handleInputChange}
          placeholder="Phone number"
          className="no-uppercase"
        />
      </div>

      <div>
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="email@example.com"
          className="no-uppercase"
        />
      </div>

      <div>
        <Label htmlFor="address">Address</Label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          placeholder="Full address"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none no-uppercase"
          rows="3"
        />
      </div>

      <div>
        <Label htmlFor="credit_limit">Credit Limit (₹) *</Label>
        <Input
          id="credit_limit"
          name="credit_limit"
          type="number"
          step="0.01"
          min="0"
          value={formData.credit_limit}
          onChange={handleInputChange}
          placeholder="Maximum credit limit"
          required
          className="no-uppercase"
        />
      </div>

      <div>
        <Label htmlFor="payment_terms">Payment Terms (Days)</Label>
        <select
          id="payment_terms"
          name="payment_terms"
          value={formData.payment_terms}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
        >
          <option value="15">Net 15 Days</option>
          <option value="30">Net 30 Days</option>
          <option value="45">Net 45 Days</option>
          <option value="60">Net 60 Days</option>
          <option value="90">Net 90 Days</option>
        </select>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          placeholder="Additional notes..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none no-uppercase"
          rows="3"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? (existingAccount ? 'Updating...' : 'Creating...') : (existingAccount ? 'Update Account' : 'Create Account')}
        </Button>
      </div>
    </form>
  );
};

// Add Credit Transaction Form Component  
const AddCreditTransactionForm = ({ account, transactionType, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(() => ({
    amount: '',
    due_date: new Date(Date.now() + (account.payment_terms || 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    description: '',
    reference_number: '',
    notes: ''
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const transactionData = {
        credit_account_id: account.id,
        type: transactionType,
        amount: parseFloat(formData.amount),
        due_date: formData.due_date,
        description: formData.description,
        reference_number: formData.reference_number.trim() || null,
        notes: formData.notes.trim() || null,
        status: 'outstanding'
      };

      const response = await apiFetch(`${backendUrl}/api/credit-transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(transactionData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${transactionType === 'receivable' ? 'Receivable' : 'Payable'} of ₹${formData.amount} added successfully!`);
        onSubmit(result);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create credit transaction');
      }
    } catch (error) {
      console.error('Error creating credit transaction:', error);
      toast.error('Failed to create credit transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const maxAmount = transactionType === 'receivable' 
    ? account.credit_limit - account.outstanding_amount
    : account.credit_limit;

  return (
    <div className="space-y-4">
      {/* Account Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-2">{account.name}</h4>
        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
          <div>Type: {account.type}</div>
          <div>Credit Limit: ₹{account.credit_limit?.toLocaleString('en-IN')}</div>
          <div>Outstanding: ₹{account.outstanding_amount?.toLocaleString('en-IN')}</div>
          <div>Available: ₹{(account.credit_limit - account.outstanding_amount).toLocaleString('en-IN')}</div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="amount">Amount (₹) *</Label>
          <Input
            id="amount"
            name="amount"
            type="number"
            step="0.01"
            min="0.01"
            max={maxAmount}
            value={formData.amount}
            onChange={handleInputChange}
            placeholder={`Max: ₹${maxAmount.toLocaleString('en-IN')}`}
            required
            className="no-uppercase"
          />
        </div>

        <div>
          <Label htmlFor="due_date">Due Date *</Label>
          <Input
            id="due_date"
            name="due_date"
            type="date"
            value={formData.due_date}
            onChange={handleInputChange}
            required
            className="no-uppercase"
          />
        </div>

        <div>
          <Label htmlFor="description">Description *</Label>
          <Input
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={`Description for this ${transactionType}`}
            required
            className="no-uppercase"
          />
        </div>

        <div>
          <Label htmlFor="reference_number">Reference Number</Label>
          <Input
            id="reference_number"
            name="reference_number"
            value={formData.reference_number}
            onChange={handleInputChange}
            placeholder="Invoice/bill number, etc."
            className="no-uppercase"
          />
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Additional notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none no-uppercase"
            rows="3"
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className={transactionType === 'receivable' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {isSubmitting ? 'Adding...' : `Add ${transactionType === 'receivable' ? 'Receivable' : 'Payable'}`}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Supplier Statement View Component
const LedgerStatement = ({ statementData, title, partyLabel, startDate, endDate, loading, onApplyFilter, onClose, showBillTypeFilter = false }) => {
  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '\u2014');

  const {
    account,
    ledger = [],
    opening_balance = 0,
    closing_balance = 0,
    totals = { total_debit: 0, total_credit: 0 },
    aging_report,
  } = statementData || {};

  const [fromDate, setFromDate] = useState(startDate || '');
  const [toDate, setToDate] = useState(endDate || '');
  const [billType, setBillType] = useState('all');
  const [drill, setDrill] = useState(null);
  const [expanded, setExpanded] = useState({});

  const allExpanded = ledger.length > 0 && ledger.every((_, i) => expanded[i]);
  const toggleExpand = (i) => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }));
  const toggleExpandAll = () => {
    if (allExpanded) { setExpanded({}); }
    else { const all = {}; ledger.forEach((_, i) => { all[i] = true; }); setExpanded(all); }
  };

  const methodLabel = (m) => (m ? String(m).replace(/_/g, ' ') : '\u2014');

  // Compact one-line summary shown under each row's particulars.
  const inlineSummary = (e) => {
    const d = e.details || {};
    const kind = d.kind || e.source_type;
    if (kind === 'purchase_order') {
      const n = (d.items || []).length;
      return `${n} item${n === 1 ? '' : 's'} \u00b7 ${d.status || 'order'} \u00b7 Paid ${formatCurrency(d.paid_amount)} / ${formatCurrency(d.total_amount)}`;
    }
    if (kind === 'po_payment') return `Via ${methodLabel(d.payment_method)}${d.bank_account ? ' \u00b7 ' + d.bank_account : ''}${d.reference ? ' \u00b7 Ref ' + d.reference : ''}`;
    if (kind === 'opening_payment') return `Opening settlement \u00b7 ${methodLabel(d.payment_method)}${d.reference ? ' \u00b7 Ref ' + d.reference : ''}`;
    if (kind === 'payment') return `Payment against ${d.reference || 'payable'}`;
    if (kind === 'payable') return `Due ${formatDate(d.due_date)} \u00b7 ${d.status || 'outstanding'} \u00b7 Remaining ${formatCurrency(d.remaining)}`;
    return null;
  };

  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4"><span className="text-gray-500">{label}</span><span className="text-right">{value ?? '\u2014'}</span></div>
  );

  // Full detail block (used both inline-expanded and in the dialog).
  const renderTxnDetail = (e) => {
    const d = e?.details || {};
    const kind = d.kind || e?.source_type;
    if (kind === 'purchase_order') {
      return (
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <Row label="PO Number" value={d.po_number} />
            <Row label="Status" value={d.status} />
            <Row label="Payment Status" value={d.payment_status || 'pending'} />
            <Row label="Order Date" value={formatDate(d.order_date)} />
            {d.expected_delivery_date && <Row label="Expected Delivery" value={formatDate(d.expected_delivery_date)} />}
            {d.job_card_number && <Row label="Job Card" value={d.job_card_number} />}
          </div>
          {Array.isArray(d.items) && d.items.length > 0 && (
            <div className="border rounded mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100"><tr className="text-left">
                  <th className="p-1.5">Part</th><th className="p-1.5">Part No.</th>
                  <th className="p-1.5 text-right">Qty</th><th className="p-1.5 text-right">Rate</th><th className="p-1.5 text-right">Amount</th>
                </tr></thead>
                <tbody>
                  {d.items.map((it, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-1.5">{it.part_name}</td>
                      <td className="p-1.5 text-gray-500">{it.part_number || '\u2014'}</td>
                      <td className="p-1.5 text-right">{it.quantity}</td>
                      <td className="p-1.5 text-right">{formatCurrency(it.unit_cost)}</td>
                      <td className="p-1.5 text-right">{formatCurrency(it.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 border-t pt-2">
            {d.subtotal != null && <Row label="Subtotal" value={formatCurrency(d.subtotal)} />}
            {d.tax_amount != null && <Row label="Tax" value={formatCurrency(d.tax_amount)} />}
            <Row label="Total" value={<span className="font-semibold">{formatCurrency(d.total_amount)}</span>} />
            <Row label="Paid" value={formatCurrency(d.paid_amount)} />
            <Row label="Remaining" value={formatCurrency(d.remaining_amount)} />
          </div>
          {d.notes && <p className="text-xs text-gray-500 border-t pt-2">Notes: {d.notes}</p>}
        </div>
      );
    }
    if (kind === 'po_payment' || kind === 'payment' || kind === 'opening_payment') {
      return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <Row label="Amount" value={<span className="font-semibold text-green-600">{formatCurrency(d.amount ?? e.credit)}</span>} />
          <Row label="Payment Method" value={<span className="capitalize">{methodLabel(d.payment_method)}</span>} />
          {d.bank_account && <Row label="Bank Account" value={d.bank_account} />}
          {d.po_number && <Row label="Purchase Order" value={d.po_number} />}
          <Row label="Reference" value={d.reference} />
          <Row label="Date" value={formatDate(d.payment_date || e.date)} />
          {d.notes && <div className="col-span-2"><Row label="Notes" value={d.notes} /></div>}
        </div>
      );
    }
    if (kind === 'payable') {
      return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <div className="col-span-2"><Row label="Description" value={d.description} /></div>
          <Row label="Reference" value={d.reference} />
          <Row label="Due Date" value={formatDate(d.due_date)} />
          <Row label="Amount" value={formatCurrency(d.amount)} />
          <Row label="Paid" value={formatCurrency(d.paid_amount)} />
          <Row label="Remaining" value={<span className="font-semibold text-orange-600">{formatCurrency(d.remaining)}</span>} />
          <Row label="Status" value={<span className="capitalize">{d.status}</span>} />
          {d.notes && <div className="col-span-2"><Row label="Notes" value={d.notes} /></div>}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <Row label="Date" value={formatDate(e?.date)} />
        <Row label="Reference" value={e?.reference} />
        <div className="col-span-2"><Row label="Description" value={e?.description} /></div>
        <Row label="Debit" value={formatCurrency(e?.debit)} />
        <Row label="Credit" value={formatCurrency(e?.credit)} />
      </div>
    );
  };

  const handleRowClick = (entry) => {
    setDrill({ entry });
  };

  const applyThisMonth = () => {
    const d = new Date();
    const s = toLocalISODate(new Date(d.getFullYear(), d.getMonth(), 1));
    const e = toLocalISODate(new Date());
    setFromDate(s);
    setToDate(e);
    onApplyFilter(s, e, billType);
  };

  const handleBillTypeChange = (v) => {
    setBillType(v);
    onApplyFilter(fromDate, toDate, v);
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Particulars', 'Reference', 'Debit', 'Credit', 'Balance'];
    const rows = [
      ['', 'Opening Balance', '', '', '', opening_balance],
      ...ledger.map((e) => [formatDate(e.date), e.description || '', e.reference || '', e.debit || 0, e.credit || 0, e.balance || 0]),
      ['', 'Closing Balance', '', totals.total_debit, totals.total_credit, closing_balance],
    ];
    exportRowsToCSV(`${title.replace(/\s+/g, '_')}_${account?.name || 'statement'}`, headers, rows);
  };

  return (
    <div className="space-y-5" data-testid="ledger-statement">
      {/* Period filter */}
      <div className="flex flex-wrap items-end gap-3 bg-gray-50 p-3 rounded-lg print:hidden">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1" data-testid="ledger-from-date" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1" data-testid="ledger-to-date" />
        </div>
        <Button size="sm" onClick={() => onApplyFilter(fromDate, toDate, billType)} disabled={loading} data-testid="ledger-apply-filter">
          {loading ? 'Loading...' : 'Apply'}
        </Button>
        <Button size="sm" variant="outline" onClick={applyThisMonth} data-testid="ledger-this-month">This Month</Button>
        {showBillTypeFilter && (
          <div>
            <Label className="text-xs">Bill Type</Label>
            <Select value={billType} onValueChange={handleBillTypeChange}>
              <SelectTrigger className="w-[160px] mt-1" data-testid="ledger-bill-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="gst">GST Bills</SelectItem>
                <SelectItem value="non_gst">Non-GST</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="text-center border-b pb-3">
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="text-gray-600">{account?.name}</p>
        <p className="text-xs text-gray-500">Period: {formatDate(fromDate)} {'\u2014'} {formatDate(toDate)}</p>
        {showBillTypeFilter && billType !== 'all' && (
          <p className="text-xs font-medium text-indigo-600 mt-1" data-testid="ledger-bill-type-badge">
            Filtered: {billType === 'gst' ? 'GST Bills Only' : 'Non-GST Only'}
          </p>
        )}
      </div>

      {/* Party info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><span className="text-gray-500">{partyLabel}:</span> <span className="font-medium">{account?.name}</span></div>
        <div><span className="text-gray-500">Contact:</span> {account?.contact || '\u2014'}</div>
        <div><span className="text-gray-500">Email:</span> {account?.email || '\u2014'}</div>
        <div><span className="text-gray-500">Terms:</span> {account?.payment_terms ?? '\u2014'} days</div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-gray-700">{formatCurrency(opening_balance)}</p>
          <p className="text-xs text-gray-500">Opening Balance</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-blue-600">{formatCurrency(totals.total_debit)}</p>
          <p className="text-xs text-gray-500">Total Debit</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-green-600">{formatCurrency(totals.total_credit)}</p>
          <p className="text-xs text-gray-500">Total Credit</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-orange-600">{formatCurrency(closing_balance)}</p>
          <p className="text-xs text-gray-500">Closing Balance</p>
        </CardContent></Card>
      </div>

      {/* Ledger table */}
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className="p-2">Date</th>
              <th className="p-2">Particulars</th>
              <th className="p-2">Reference</th>
              <th className="p-2 text-right">Debit</th>
              <th className="p-2 text-right">Credit</th>
              <th className="p-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-50 font-medium border-t">
              <td className="p-2" colSpan={5}>Opening Balance</td>
              <td className="p-2 text-right">{formatCurrency(opening_balance)}</td>
            </tr>
            {ledger.length === 0 ? (
              <tr><td className="p-4 text-center text-gray-500" colSpan={6}>No transactions in this period</td></tr>
            ) : (
              ledger.map((e, i) => (
                <React.Fragment key={i}>
                <tr className="border-t hover:bg-blue-50 cursor-pointer" onClick={() => handleRowClick(e)} data-testid={`ledger-row-${i}`}>
                  <td className="p-2 whitespace-nowrap align-top">{formatDate(e.date)}</td>
                  <td className="p-2">
                    <div className="flex items-start gap-1">
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); toggleExpand(i); }}
                        className="mt-0.5 text-gray-400 hover:text-gray-700 print:hidden"
                        data-testid={`ledger-expand-${i}`}
                        aria-label="Toggle details"
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${expanded[i] ? '' : '-rotate-90'}`} />
                      </button>
                      <div>
                        <div>{e.description}</div>
                        {inlineSummary(e) && (
                          <div className="text-xs text-gray-500 mt-0.5" data-testid={`ledger-inline-summary-${i}`}>{inlineSummary(e)}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-2 text-blue-600 align-top">{e.reference}</td>
                  <td className="p-2 text-right align-top">{e.debit ? formatCurrency(e.debit) : '\u2014'}</td>
                  <td className="p-2 text-right align-top">{e.credit ? formatCurrency(e.credit) : '\u2014'}</td>
                  <td className="p-2 text-right font-medium align-top">{formatCurrency(e.balance)}</td>
                </tr>
                {expanded[i] && (
                  <tr className="bg-gray-50" data-testid={`ledger-detail-${i}`}>
                    <td className="p-3" colSpan={6}>
                      <div className="rounded border bg-white p-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Transaction Detail</p>
                        {renderTxnDetail(e)}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))
            )}
            <tr className="bg-gray-100 font-bold border-t-2">
              <td className="p-2" colSpan={3}>Closing Balance</td>
              <td className="p-2 text-right">{formatCurrency(totals.total_debit)}</td>
              <td className="p-2 text-right">{formatCurrency(totals.total_credit)}</td>
              <td className="p-2 text-right">{formatCurrency(closing_balance)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Aging report (kept) */}
      {aging_report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-sm">
          <div><p className="font-semibold text-green-600">{formatCurrency(aging_report.current)}</p><p className="text-xs text-gray-500">Current</p></div>
          <div><p className="font-semibold text-yellow-600">{formatCurrency(aging_report["30_days"])}</p><p className="text-xs text-gray-500">1-30 Days</p></div>
          <div><p className="font-semibold text-orange-600">{formatCurrency(aging_report["60_days"])}</p><p className="text-xs text-gray-500">31-60 Days</p></div>
          <div><p className="font-semibold text-red-600">{formatCurrency(aging_report["90_plus_days"])}</p><p className="text-xs text-gray-500">90+ Days</p></div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-3 border-t print:hidden">
        <Button variant="outline" onClick={toggleExpandAll} data-testid="ledger-expand-all-btn">
          {allExpanded ? 'Collapse All' : 'Expand All Details'}
        </Button>
        <Button variant="outline" onClick={handleExportCSV} data-testid="ledger-export-csv-btn">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="ledger-print-btn">
          <FileCheck className="h-4 w-4 mr-2" /> Print / Save PDF
        </Button>
        <Button onClick={onClose} data-testid="ledger-close-btn">Close</Button>
      </div>

      {/* Drill-through detail dialog (uses embedded details, printable) */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-lg" data-testid="ledger-detail-dialog">
          <DialogHeader>
            <DialogTitle>Transaction Detail</DialogTitle>
            <DialogDescription>
              {drill?.entry?.description} {drill?.entry?.reference ? `\u00b7 ${drill.entry.reference}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div id="txn-detail-print">
            {drill?.entry && renderTxnDetail(drill.entry)}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="ledger-detail-print-btn">
              <FileCheck className="h-4 w-4 mr-2" /> Print
            </Button>
            <Button size="sm" onClick={() => setDrill(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SupplierStatementView = ({ statementData, startDate, endDate, loading, onApplyFilter, onClose }) => (
  <LedgerStatement key={`${startDate}-${endDate}`} statementData={statementData} title="SUPPLIER STATEMENT" partyLabel="Supplier"
    startDate={startDate} endDate={endDate} loading={loading} onApplyFilter={onApplyFilter} onClose={onClose} showBillTypeFilter />
);

const CustomerStatementView = ({ statementData, startDate, endDate, loading, onApplyFilter, onClose }) => (
  <LedgerStatement key={`${startDate}-${endDate}`} statementData={statementData} title="CUSTOMER STATEMENT" partyLabel="Customer"
    startDate={startDate} endDate={endDate} loading={loading} onApplyFilter={onApplyFilter} onClose={onClose} />
);

export { AccountingSection, ReportsSection, LedgerStatement };
